'use client';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import React from 'react';
import {
   Box,
   Flex,
   Table,
   Tbody,
   Td,
   Text,
   Tr,
   useColorModeValue,
   useDisclosure,
   Checkbox,
   Drawer,
   DrawerOverlay,
   DrawerContent,
   DrawerCloseButton,
   DrawerHeader,
   DrawerBody,
   useOutsideClick,
   Spinner,
   InputGroup,
   Thead,
   Th,
   Grid,
   GridItem,
   Input,
   Button,
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalFooter,
   ModalBody,
   ModalCloseButton,
   Image,
   IconButton,
   Tooltip,
   Popover,
   PopoverTrigger,
   PopoverContent,
   PopoverBody,
   VStack,
   useColorMode,
} from '@chakra-ui/react';
import Select from 'react-select';
import { motion } from 'framer-motion';
import SalesDetails from 'components/salesDetails/SalesDetails';
import { SearchIcon, ViewIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import Columnshowhide from '../components/Columnshowhide';
import SalesPurchaseFilter from '../components/SalesPurchaseFilter';
import {
   createColumnHelper,
   flexRender,
   getCoreRowModel,
   getSortedRowModel,
   useReactTable,
} from '@tanstack/react-table';
import moment from 'moment';
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx-js-style';

// Components
import Card from 'components/card/Card';
import PeriodDatePicker from 'components/RangePicker/PeriodDatePicker';
import { renderDynamicDropdown } from 'components/ui/DynamicDropdown';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import { getCompanies } from '../../../../redux/actions/company';
import { deleteSaleInvoice, getSingleWithPrevAndNext } from '../../../../redux/actions/salesInvoice';
import { deleteReportFormat, getReportFormat, getReportSaleDetail } from '../../../../redux/actions/reports';

// Utils
import { getShareCompanyOptions } from 'utils/util';
import { deleteRecord } from 'utils/util';
import { REPORT_TYPE } from 'constants';

// Assets
import nodata from 'assets/img/nfts/no data.svg';
import Download from '../../../../assets/img/nfts/download.svg';
import xls from '../../../../../src/assets/img/nfts/xls.svg';
import back from '../../../../../src/assets/img/nfts/back.svg';
import cancel from '../../../../assets/img/nfts/cancel.svg';
import DotIcon from '../../../../assets/img/nfts/dot.svg';
import { MdCompress, MdDelete, MdExpand, MdOutlineRefresh } from 'react-icons/md';
import { useGlobal } from 'contexts';
import { filter } from 'lodash';
import { IoFilterOutline } from 'react-icons/io5';
import ColumnShowHide from '../components/Columnshowhide';
import { LucideEdit } from 'lucide-react';
import { FiPlusCircle } from 'react-icons/fi';
import { getSingleSalesReturnWithPrevAndNext } from '../../../../redux/actions/salesReturnInvoice';
import { useUser } from 'contexts';
import Loader from 'views/admin/default/Loader';

// Constants
const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_DELAY = 150;
const REPORT_TYPE_OPTIONS = [
   { value: 'date', label: 'Date' },
   { value: 'party', label: 'Party' },
   { value: 'broker', label: 'Broker' },
];
const FORMAT_TYPE_OPTIONS = [
   { value: 'with_item_detail', label: 'With Item Detail' },
   { value: 'without_item_detail', label: 'Without Item Detail' },
];

const MotionFlex = motion(Flex);
const MotionSearchIcon = motion(SearchIcon);

// Helper Components
const ColumnVisibilityModal = React.memo(({ isOpen, onClose, columns, columnVisibility, setColumnVisibility }) => (
   <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
         <ModalHeader>Show/Hide Columns</ModalHeader>
         <ModalCloseButton />
         <ModalBody>
            {columns.map((column) => (
               <Flex key={column.id} mb={2} alignItems="center">
                  <Checkbox
                     isChecked={!!columnVisibility[column.id]}
                     onChange={(e) => {
                        setColumnVisibility((prev) => ({
                           ...prev,
                           [column.id]: e.target.checked,
                        }));
                     }}
                     mr={2}
                  />
                  <Text>{column.header}</Text>
               </Flex>
            ))}
         </ModalBody>
         <ModalFooter>
            <Button colorScheme="blue" onClick={onClose}>
               Close
            </Button>
         </ModalFooter>
      </ModalContent>
   </Modal>
));

const SalesReturnDetail = () => {
   // Refs
   const fromDateInputRef = useRef(null);
   const wrapperRef = useRef();
   const searchTimeoutRef = useRef(null);
   const abortControllerRef = useRef(null);
   const dataCacheRef = useRef({});

   // State
   const [companyFilter, setCompanyFilter] = useState([]);
   const [reportType, setReportType] = useState('date');
   const [formatType, setFormatType] = useState('with_item_detail');
   const [expandedItems, setExpandedItems] = useState({});
   const [sorting, setSorting] = useState([]);
   const [columnVisibility, setColumnVisibility] = useState({});
   const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);
   const [isShowHideColumn, setIsShowHideColumn] = useState(false);
   const [searchTerm, setSearchTerm] = useState('');
   const [isLoading, setIsLoading] = useState(false);
   const [expanded, setExpanded] = useState(false);
   const [isEditShowHide, setIsEditShowHide] = useState(false);
   const [editData, setEditData] = useState({});
   const [filteredResults, setFilteredResults] = useState(null);
   const [flattenedData, setFlattenedData] = useState([]);
   const [allExpanded, setAllExpanded] = useState(false);
   const [isModalLoading, setIsModalLoading] = useState(false);
   const [dateRange, setDateRange] = useState(() => {
      const today = new Date();
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(today.getDate() - 6);

      return {
         fromDate: sevenDaysAgo.toISOString().split('T')[0], // last 7th day
         toDate: today.toISOString().split('T')[0], // today
      };
   });

   // Redux
   const dispatch = useDispatch();
   const company = useSelector((state) => state.company.list || []);
   const salesReport = useSelector((state) => state.reports?.list || []);
   const reportformat = useSelector((state) => state.reports?.reportFormat || []);
   const viewData = useSelector((state) => state.salesReturnInvoice?.saleReturnData);
   const { viewDetail, paginate } = useMemo(() => {
      const data = viewData?.data[0];

      const pagination = viewData?.pagination;

      return { viewDetail: data, paginate: pagination };
   }, [viewData?.data, viewData?.pagination]);
   const [isEditFormOpen, setIsEditFormOpen] = React.useState(false);
   const [isOpenPurchaseReturnSingleView, setIsOpenPurchaseReturnSingleView] = React.useState(null);

   const dbName = localStorage.getItem('dbName');
   const { isOpen, onOpen, onClose } = useDisclosure();

   // Theme and styles
   const textColor = useColorModeValue('secondaryGray.900', 'white');
   const backgroundColor = useColorModeValue('white', '#101F4A');
   const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
   const bg = useColorModeValue('#f2f7f2', '#1a254b');
   const icon = useColorModeValue('#000000', '#ffffff');
   const controlBg = useColorModeValue('#FFFFFF', '#2D3748');
   const controlBorder = useColorModeValue('#E2E8F0', '#4A5568');
   const controlBorderHover = useColorModeValue('#CBD5E0', '#718096');
   const focusBoxShadow = useColorModeValue('0 0 0 1px #3182CE', '0 0 0 1px #63B3ED');
   const placeholderColor = useColorModeValue('#A0AEC0', '#718096');
   const optionBg = useColorModeValue('#FFFFFF', '#2D3748');
   const optionHoverBg = useColorModeValue('#EDF2F7', '#4A5568');
   const menuBg = useColorModeValue('#FFFFFF', '#2D3748');
   const menuBorder = useColorModeValue('#E2E8F0', '#4A5568');
   const indicatorColor = useColorModeValue('#718096', '#A0AEC0');
   const indicatorHoverColor = useColorModeValue('#4A5568', '#CBD5E0');
   const isStaticReportType = ['date', 'party', 'broker'].includes(reportType);
   const { companyList } = useGlobal();
   const { userGlobalSettingsData } = useGlobal();
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   // Memoized values
   const companyOptions = useMemo(() => {
      return getShareCompanyOptions({ dbName, company: companyList });
   }, [companyList, dbName]);
   const { user } = useUser();
   const companyId = Number(localStorage.getItem('companyId'));
   const userRights = user?.user?.userRights || user?.userRights;
   const saleInvoiceRight = Array.isArray(userRights)
      ? userRights.find((right) => right.companyId === companyId)?.saleinvoice
      : null;
   const saleReturnRight = Array.isArray(userRights)
      ? userRights.find((right) => right.companyId === companyId)?.salereturn
      : null;
   const lastcompanyOptions = useMemo(
      () =>
         companyOptions.map((company) => ({
            value: company.value,
            label: company.label,
            id: company.value,
            name: company.label,
         })),
      [companyOptions],
   );
   const { isOpen: isFilterOpen, onOpen: onFilterOpen, onClose: onFilterClose } = useDisclosure();
   const companyIds = useMemo(
      () =>
         companyFilter.length > 0
            ? companyFilter
            : localStorage.getItem('companyId')
            ? [parseInt(localStorage.getItem('companyId'))]
            : [],
      [companyFilter],
   );
   const [showHideData, setShowHideData] = useState({});

   const allowedDefNames = ['SalesReturnDetailDate', 'SalesReturnDetailParty', 'SalesReturnDetailBroker'];
   const dynamicOptions = reportformat
      .filter((item) => item.defName && allowedDefNames.includes(item.defName))
      .map((item) => ({
         id: item.id,
         value: item.defName && item.title,
         label: item.title,
         isDynamic: true,
         formatData: item,
      }));

   const dynamicValues = dynamicOptions.map((option) => option.value);
   const dynamicOptionValues = dynamicOptions.find((option) => option.value === reportType);
   const dynamicEditOption = dynamicOptionValues?.formatData.defName;
   const handleReportTypeChange = (selectedOption) => {
      setReportType(selectedOption.value);
      setExpandedItems({});
      dispatch(getReportFormat());
   };

   const getColumnsReportsSalesReturnDetail = useCallback((formatType, reportType) => {
      const isPartyReport = reportType === 'party' || dynamicEditOption === 'SalesReturnDetailParty';
      const isDateReport = dynamicEditOption === 'SalesReturnDetailDate';
      // Use the passed selectedFormat or fallback to finding from reportformat array
      // const formatToUse = dynamicOptionValues || dynamicOptionValues.formatData;

      const viewJson = dynamicOptionValues?.formatData?.viewJson || [];

      const convertBackendColor = (backendColor) => {
         if (!backendColor) return null;
         if (backendColor.startsWith('#')) return backendColor;
         if (backendColor.startsWith('0xff')) return '#' + backendColor.substring(4);
         if (/^[0-9A-Fa-f]{6}$/.test(backendColor)) return '#' + backendColor;
         return backendColor;
      };

      const getColumnInfo = (defName) => {
         // First try to find by defName, then by title
         const viewItem = viewJson.find(
            (item) =>
               item?.defName?.toLowerCase() === defName.toLowerCase() ||
               item?.title?.toLowerCase() === defName.toLowerCase(),
         );

         if (!viewItem || isStaticReportType) {
            // Return default values if no customization found or for static report types
            return {
               title: defName,
               isShow: true,
               color: textColor,
               isBold: false,
               isItalic: false,
               orderNo: 0,
               flex: 1,
            };
         }

         return {
            title: viewItem.title || defName,
            isShow: viewItem.isShow === 'true' || viewItem.isShow === true,
            color: viewItem.color ? convertBackendColor(viewItem.color) : textColor,
            isBold: viewItem.isBold === 'true' || viewItem.isBold === true,
            isItalic: viewItem.isItalic === 'true' || viewItem.isItalic === true,
            orderNo: viewItem.orderNo ? parseInt(viewItem.orderNo) : 0,
            flex: viewItem.flex ? parseInt(viewItem.flex) : 1,
         };
      };

      const baseColumns = [
         {
            id: 'transactionAt',
            header: getColumnInfo('date').title,
            accessor: 'transactionAt',
            isShow: getColumnInfo('date').isShow,
            color: getColumnInfo('date').color,
            isBold: getColumnInfo('date').isBold,
            isItalic: getColumnInfo('date').isItalic,
            orderNo: getColumnInfo('date').orderNo,
            flex: getColumnInfo('date').flex,
            cell: (info) => {
               const value = info.getValue();
               return value ? moment(value).format('DD-MM-YYYY') : value;
            },
         },
         {
            id: 'docno',
            header: getColumnInfo('doc').title,
            accessor: 'docno',
            isShow: getColumnInfo('doc').isShow,
            color: getColumnInfo('doc').color,
            isBold: getColumnInfo('doc').isBold,
            isItalic: getColumnInfo('doc').isItalic,
            orderNo: getColumnInfo('doc').orderNo,
            flex: getColumnInfo('doc').flex,
         },
         ...(!isPartyReport
            ? [
                 {
                    id: 'accountname',
                    header: getColumnInfo('party').title,
                    accessor: 'accountname',
                    isShow: getColumnInfo('party').isShow,
                    color: getColumnInfo('party').color,
                    isBold: getColumnInfo('party').isBold,
                    isItalic: getColumnInfo('party').isItalic,
                    orderNo: getColumnInfo('party').orderNo,
                    flex: getColumnInfo('party').flex,
                 },
              ]
            : []),
         ...(reportType === 'date'
            ? [
                 {
                    id: 'mobile',
                    header: getColumnInfo('mobile').title,
                    accessor: 'mobile',
                    isShow: getColumnInfo('mobile').isShow,
                    color: getColumnInfo('mobile').color,
                    isBold: getColumnInfo('mobile').isBold,
                    isItalic: getColumnInfo('mobile').isItalic,
                    orderNo: getColumnInfo('mobile').orderNo,
                    flex: getColumnInfo('mobile').flex,
                 },
              ]
            : []),
         {
            id: 'daybookname',
            header: getColumnInfo('daybook').title,
            accessor: 'daybookname',
            isShow: getColumnInfo('daybook').isShow,
            color: getColumnInfo('daybook').color,
            isBold: getColumnInfo('daybook').isBold,
            isItalic: getColumnInfo('daybook').isItalic,
            orderNo: getColumnInfo('daybook').orderNo,
            flex: getColumnInfo('daybook').flex,
         },
         {
            id: 'salesmanname',
            header: getColumnInfo('salesman').title,
            accessor: 'salesmanname',
            isShow: getColumnInfo('salesman').isShow,
            color: getColumnInfo('salesman').color,
            isBold: getColumnInfo('salesman').isBold,
            isItalic: getColumnInfo('salesman').isItalic,
            orderNo: getColumnInfo('salesman').orderNo,
            flex: getColumnInfo('salesman').flex,
         },
         {
            id: 'notes',
            header: getColumnInfo('notes').title,
            accessor: 'notes',
            isShow: getColumnInfo('notes').isShow,
            color: getColumnInfo('notes').color,
            isBold: getColumnInfo('notes').isBold,
            isItalic: getColumnInfo('notes').isItalic,
            orderNo: getColumnInfo('notes').orderNo,
            flex: getColumnInfo('notes').flex,
         },
         {
            id: 'qty1',
            header: getColumnInfo('qty').title,
            accessor: 'qty1',
            isNumeric: true,
            isShow: getColumnInfo('qty').isShow,
            color: getColumnInfo('qty').color,
            isBold: getColumnInfo('qty').isBold,
            isItalic: getColumnInfo('qty').isItalic,
            orderNo: getColumnInfo('qty').orderNo,
            flex: getColumnInfo('qty').flex,
         },
         {
            id: 'netAmount',
            header: getColumnInfo('netAmt').title,
            accessor: 'netAmount',
            isNumeric: true,
            isShow: getColumnInfo('netAmt').isShow,
            color: getColumnInfo('netAmt').color,
            isBold: getColumnInfo('netAmt').isBold,
            isItalic: getColumnInfo('netAmt').isItalic,
            orderNo: getColumnInfo('netAmt').orderNo,
            flex: getColumnInfo('netAmt').flex,
            cell: (info) => {
               const value = info.getValue();
               return `₹${Number(value || 0).toLocaleString()}`;
            },
         },
         {
            id: 'netAmount',
            header: getColumnInfo('billAmt').title,
            accessor: 'netAmount',
            isNumeric: true,
            isShow: getColumnInfo('billAmt').isShow,
            color: getColumnInfo('billAmt').color,
            isBold: getColumnInfo('billAmt').isBold,
            isItalic: getColumnInfo('billAmt').isItalic,
            orderNo: getColumnInfo('billAmt').orderNo,
            flex: getColumnInfo('billAmt').flex,
            cell: (info) => {
               const value = info.getValue();
               return `₹${Number(value || 0).toLocaleString()}`;
            },
         },
      ];

      if (formatType === 'with_item_detail') {
         let itemDetailColumns = [];

         if (reportType === 'date') {
            itemDetailColumns = [
               {
                  id: 'itemName',
                  header: getColumnInfo('itemName').title,
                  accessor: 'itemName',
                  isShow: getColumnInfo('itemName').isShow,
                  color: getColumnInfo('itemName').color,
                  isBold: getColumnInfo('itemName').isBold,
                  isItalic: getColumnInfo('itemName').isItalic,
                  orderNo: getColumnInfo('itemName').orderNo,
                  flex: getColumnInfo('itemName').flex,
                  cell: (info) => {
                     const value = info.getValue();
                     return (
                        <div
                           className="text-center px-2 py-1"
                           style={{
                              textAlign: 'center',
                              wordWrap: 'break-word',
                              whiteSpace: 'normal',
                              maxWidth: '200px',
                              minWidth: '150px',
                           }}
                           title={value}>
                           {value}
                        </div>
                     );
                  },
               },
               {
                  id: 'serialNo',
                  header: getColumnInfo('serialNo').title,
                  accessorKey: 'serialNo',
                  isShow: getColumnInfo('serialNo').isShow,
                  color: getColumnInfo('serialNo').color,
                  isBold: getColumnInfo('serialNo').isBold,
                  isItalic: getColumnInfo('serialNo').isItalic,
                  orderNo: getColumnInfo('serialNo').orderNo,
                  flex: getColumnInfo('serialNo').flex,
               },
               {
                  id: 'itemRate',
                  header: getColumnInfo('rate').title,
                  accessor: 'itemRate',
                  isNumeric: true,
                  isShow: getColumnInfo('rate').isShow,
                  color: getColumnInfo('rate').color,
                  isBold: getColumnInfo('rate').isBold,
                  isItalic: getColumnInfo('rate').isItalic,
                  orderNo: getColumnInfo('rate').orderNo,
                  flex: getColumnInfo('rate').flex,
               },
            ];
         } else {
            itemDetailColumns = [
               {
                  id: 'itemName',
                  header: getColumnInfo('itemName').title,
                  accessor: 'itemName',
                  isShow: getColumnInfo('itemName').isShow,
                  color: getColumnInfo('itemName').color,
                  isBold: getColumnInfo('itemName').isBold,
                  isItalic: getColumnInfo('itemName').isItalic,
                  orderNo: getColumnInfo('itemName').orderNo,
                  flex: getColumnInfo('itemName').flex,
                  cell: (info) => {
                     const value = info.getValue();
                     return (
                        <div
                           className="text-center px-2 py-1"
                           style={{
                              textAlign: 'center',
                              wordWrap: 'break-word',
                              whiteSpace: 'normal',
                              maxWidth: '200px',
                              minWidth: '150px',
                           }}
                           title={value}>
                           {value}
                        </div>
                     );
                  },
               },
               {
                  id: 'serialNo',
                  header: getColumnInfo('serialNo').title,
                  accessorKey: 'serialNo',
                  isShow: getColumnInfo('serialNo').isShow,
                  color: getColumnInfo('serialNo').color,
                  isBold: getColumnInfo('serialNo').isBold,
                  isItalic: getColumnInfo('serialNo').isItalic,
                  orderNo: getColumnInfo('serialNo').orderNo,
                  flex: getColumnInfo('serialNo').flex,
               },
               {
                  id: 'itemRate',
                  header: getColumnInfo('rate').title,
                  accessor: 'itemRate',
                  isNumeric: true,
                  isShow: getColumnInfo('rate').isShow,
                  color: getColumnInfo('rate').color,
                  isBold: getColumnInfo('rate').isBold,
                  isItalic: getColumnInfo('rate').isItalic,
                  orderNo: getColumnInfo('rate').orderNo,
                  flex: getColumnInfo('rate').flex,
               },
               ...(!isDateReport
                  ? [
                       {
                          id: 'hsnSac',
                          header: getColumnInfo('HSN/SAC').title || 'HSN/SAC',
                          accessor: 'hsnSac',
                          isShow: getColumnInfo('HSN/SAC').isShow,
                          color: getColumnInfo('HSN/SAC').color,
                          isBold: getColumnInfo('HSN/SAC').isBold,
                          isItalic: getColumnInfo('HSN/SAC').isItalic,
                          orderNo: getColumnInfo('HSN/SAC').orderNo,
                          flex: getColumnInfo('HSN/SAC').flex,
                       },
                       {
                          id: 'dueDays',
                          header: getColumnInfo('Due Days').title || 'Due Days',
                          accessor: 'dueDays',
                          isShow: getColumnInfo('Due Days').isShow,
                          color: getColumnInfo('Due Days').color,
                          isBold: getColumnInfo('Due Days').isBold,
                          isItalic: getColumnInfo('Due Days').isItalic,
                          orderNo: getColumnInfo('Due Days').orderNo,
                          flex: getColumnInfo('Due Days').flex,
                       },
                    ]
                  : []),
            ];
         }

         // Insert item details after account/mobile columns
         const insertIndex =
            reportType === 'date'
               ? baseColumns.findIndex((col) => col.id === 'mobile') + 1
               : baseColumns.findIndex((col) => col.id === 'docno') + 1;

         baseColumns.splice(insertIndex, 0, ...itemDetailColumns);
      }

      // Filter out columns where isShow is false
      return baseColumns
         .filter((col) => col.isShow !== false)
         .sort((a, b) => {
            const orderA = a.orderNo || 0;
            const orderB = b.orderNo || 0;
            return orderA - orderB;
         });
   });

   const staticReportTypeOptions = [
      { value: 'date', label: 'Date' },
      { value: 'party', label: 'Party' },
      { value: 'broker', label: 'Broker' },
   ];

   const reportTypeOptions = [
      ...staticReportTypeOptions,
      ...(dynamicOptions.length > 0
         ? [
              {
                 label: 'Custom Formats',
                 options: dynamicOptions,
              },
           ]
         : []),
   ];
   const staticOption = staticReportTypeOptions.find((option) => option.value === reportType);

   const dynamicColumns = useMemo(() => {
      const format = reportformat.find((f) => f.id === reportType) || reportformat[0];
      return getColumnsReportsSalesReturnDetail(formatType, reportType, format);
   }, [formatType, reportType, reportformat]);

   const columnsData = useMemo(() => {
      return dynamicColumns.map((col, index) => ({
         key: col?.id,
         label: col?.header,
         title: col?.header,
         width: col?.width, // default width if not defined
         index: index + 1,
         status: true,
      }));
   }, [dynamicColumns]);

   useEffect(() => {
      // Only fetch report formats once when component mounts or when really needed
      if (reportformat.length === 0) {
         dispatch(getReportFormat());
      }
   }, [dispatch, reportformat.length]);

   const formatOptionLabel = ({ label, isDynamic, id }, { context }) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
         <span>{label}</span>
         {isDynamic && context === 'menu' && (
            <button
               onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteFormat(id);
               }}
               style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#ff4d4f',
                  padding: '0 5px',
               }}
               title="Delete this format">
               <MdDelete />
            </button>
         )}
      </div>
   );

   const handleDeleteFormat = async (id) => {
      try {
         await dispatch(deleteReportFormat(id));

         if (editData?.id === id) {
            setIsEditShowHide(false);
            setEditData({});
         }

         // Only refetch formats if we're currently using a dynamic format that was deleted
         if (dynamicValues.includes(reportType)) {
            dispatch(getReportFormat());
         }
      } catch (error) {
         console.error('Delete error:', error);
      }
   };

   useEffect(() => {
      dispatch(getReportFormat());
   }, [dispatch]);

   const handleNextPage = () => {
      console.log('Next');
   };
   const handlePrevPage = () => {
      console.log('Prev');
   };

   const calculateColumnWidths = () => {
      const visibleColumns = dynamicColumns.filter((col) => columnVisibility[col.id]);
      const totalFlex = visibleColumns.reduce((sum, col) => sum + (col.flex || 1), 0);
      const availableWidth = 100; // This represents 100% of the table width

      return visibleColumns.map((col) => {
         const flexValue = col.flex || 1;
         return `${(flexValue / totalFlex) * availableWidth}%`;
      });
   };

   const columnWidths = calculateColumnWidths();
   useEffect(() => {
      if (Object.keys(showHideData).length === 0) {
         setShowHideData(columnsData);
      }
   }, [showHideData, columnsData]);

   const flattenData = useCallback(
      (data) => {
         if (!data) return [];

         if (reportType === REPORT_TYPE.party) {
            return data.flatMap((company) => company.data?.flatMap((account) => account.data || []) || []);
         }
         return data.flatMap((dateItem) => dateItem.data?.flatMap((item) => item.data || []) || []);
      },
      [reportType],
   );

   // Table setup
   const columnHelper = createColumnHelper();
   const columns = useMemo(
      () =>
         dynamicColumns.map((column) =>
            columnHelper.accessor(column.id, {
               id: column.id,
               header: () => (
                  <Text
                     justifyContent="space-between"
                     align="center"
                     fontSize={{ sm: '10px', lg: '12px' }}
                     color={textColor}>
                     {column.header}
                  </Text>
               ),
               cell: (info) => <Flex align="center">{info.getValue()}</Flex>,
            }),
         ),
      [columnHelper, dynamicColumns, textColor],
   );

   const table = useReactTable({
      data: filteredResults || flattenedData,
      columns,
      state: { sorting, columnVisibility },
      onSortingChange: setSorting,
      onColumnVisibilityChange: setColumnVisibility,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
   });

   // Effects

   useEffect(() => {
      const initialColumnVisibility = {};
      dynamicColumns.forEach((column) => {
         initialColumnVisibility[column.id] = true;
      });
      setColumnVisibility(initialColumnVisibility);
   }, [dynamicColumns]);

   useEffect(() => {
      const storedCompanyId = localStorage.getItem('companyId');
      if (storedCompanyId && companyList.length > 0) {
         const companyId = parseInt(storedCompanyId);
         const matchingCompany = companyList.find((comp) => comp.id === companyId);
         if (matchingCompany && !companyFilter.includes(matchingCompany.id)) {
            setCompanyFilter([matchingCompany.id]);
         }
      }
   }, [companyList]);

   useEffect(() => {
      setFlattenedData(flattenData(salesReport));
   }, [salesReport, reportType, flattenData]);
   // const flattenedData = useMemo(() => {
   //    if (!salesReport) return [];
   //    return flattenData(salesReport);
   // }, [salesReport, reportType]);
   // Data fetching
   const fetchData = useCallback(
      async (searchQuery = '') => {
         // Cancel previous request
         if (abortControllerRef.current) {
            abortControllerRef.current.abort();
         }

         abortControllerRef.current = new AbortController();
         setIsLoading(true);

         try {
            const companyIds =
               companyFilter.length > 0
                  ? companyFilter
                  : localStorage.getItem('companyId')
                  ? [parseInt(localStorage.getItem('companyId'))]
                  : [];

            // Only fetch if we have valid parameters
            if (dateRange.fromDate && dateRange.toDate && companyIds.length > 0) {
               const cacheKey = `${companyIds.toString()}-${reportType}-${dateRange.fromDate}-${
                  dateRange.toDate
               }-${searchQuery}`;

               // Return cached data if available
               if (dataCacheRef.current[cacheKey]) {
                  setFilteredResults(dataCacheRef.current[cacheKey]);
                  setIsLoading(false);
                  return;
               }

               const result = await dispatch(
                  getReportSaleDetail({
                     companyIds: companyIds.toString(),
                     type: reportType,
                     isReturn: 1,
                     fromDate: dateRange.fromDate,
                     toDate: dateRange.toDate,
                     search: searchQuery.trim(),
                  }),
                  { signal: abortControllerRef.current.signal },
               );

               // Cache the results
               dataCacheRef.current[cacheKey] = result;
            }
         } catch (error) {
            if (error.name !== 'AbortError') {
               console.error('Error fetching sales details:', error);
            }
         } finally {
            setIsLoading(false);
            abortControllerRef.current = null;
         }
      },
      [dispatch, companyFilter, reportType, dateRange.fromDate, dateRange.toDate], // More specific dependencies
   );

   useEffect(() => {
      const timer = setTimeout(() => {
         fetchData();
      }, 300); // Small debounce to prevent rapid successive calls

      return () => clearTimeout(timer);
   }, [fetchData]);

   // Event handlers
   const handleSearchChange = useCallback(
      (e) => {
         const value = e.target.value;
         setSearchTerm(value);

         if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
         }

         searchTimeoutRef.current = setTimeout(() => {
            if (value === '' || value.length >= MIN_SEARCH_LENGTH) {
               // Use cached results if available for this search term
               if (dataCacheRef.current[value]) {
                  setFilteredResults(dataCacheRef.current[value]);
                  return;
               }
               fetchData(value);
            } else if (value === '') {
               // If search is cleared, show all data
               fetchData('');
            }
         }, SEARCH_DEBOUNCE_DELAY);
      },
      [fetchData],
   );

   const handleRefresh = useCallback(() => {
      // Clear cache for current parameters to force refresh
      const companyIds =
         companyFilter.length > 0
            ? companyFilter
            : localStorage.getItem('companyId')
            ? [parseInt(localStorage.getItem('companyId'))]
            : [];

      const cacheKey = `${companyIds.toString()}-${reportType}-${dateRange.fromDate}-${dateRange.toDate}-${searchTerm}`;
      delete dataCacheRef.current[cacheKey];

      fetchData(searchTerm);
   }, [fetchData, companyFilter, reportType, dateRange, searchTerm]);

   useEffect(() => {
      return () => {
         // Cleanup function to cancel any pending API calls
         if (abortControllerRef.current) {
            abortControllerRef.current.abort();
         }
         if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
         }
      };
   }, []);

   const handleCompanyChange = useCallback((selectedOptions) => {
      setCompanyFilter(selectedOptions ? selectedOptions : []);
   }, []);

   const handleDateRangeChange = useCallback((dates) => {
      if (!dates || dates.length !== 2) return;
      const [from, to] = dates.map((date) => (typeof date === 'string' ? date : date.toISOString().slice(0, 10)));
      setDateRange({ fromDate: from, toDate: to });
   }, []);

   const toggleExpand = useCallback((id, forceExpand = false) => {
      setExpandedItems((prev) => ({
         ...prev,
         [id]: forceExpand !== undefined ? forceExpand : !prev[id],
      }));
   }, []);

   const expandAll = useCallback(() => {
      const newExpanded = {};

      if (reportType === REPORT_TYPE.party) {
         // For party report type
         salesReport.forEach((companyData) => {
            const companyKey = `company-${companyData.companyId}`;
            newExpanded[companyKey] = true;

            companyData.data?.forEach((accountData) => {
               const accountKey = `account-${accountData.accountId}`;
               newExpanded[accountKey] = true;
            });
         });
      } else {
         // For other report types (date/broker)
         salesReport.forEach((dateItem) => {
            dateItem.data?.forEach((itemData) => {
               const itemKey = `item-${itemData.itemId}`;
               newExpanded[itemKey] = true;
            });
         });
      }

      setExpandedItems(newExpanded);
      setAllExpanded(true);
   }, [salesReport, reportType]);

   const collapseAll = useCallback(() => {
      const newExpanded = {};
      salesReport.forEach((companyData) => {
         const companyKey = `company-${companyData.companyId}`;
         newExpanded[companyKey] = true; // Keep companies expanded

         if (companyData.items) {
            companyData.items.forEach((itemData) => {
               const itemKey = `item-${itemData.item_id}-${companyData.companyId}`;
               newExpanded[itemKey] = false; // Collapse all items
            });
         }
      });
      setExpandedItems(newExpanded);
      setAllExpanded(false);
   }, [salesReport]);

   const initializeExpandedItems = useCallback(
      (data) => {
         if (!data || !Array.isArray(data)) return {};

         const initialExpandedState = {};

         if (reportType === REPORT_TYPE.party) {
            data.forEach((companyData) => {
               // Expand company by default
               initialExpandedState[`company-${companyData.companyId}`] = true;

               // Expand all accounts by default
               if (companyData.data && Array.isArray(companyData.data)) {
                  companyData.data.forEach((accountData) => {
                     initialExpandedState[`account-${accountData.accountId}`] = true;
                  });
               }
            });
         }

         return initialExpandedState;
      },
      [reportType],
   );

   useEffect(() => {
      if (salesReport && salesReport.length > 0) {
         const initialExpandedState = initializeExpandedItems(salesReport);
         setExpandedItems(initialExpandedState);
      }
   }, [salesReport, initializeExpandedItems]);

   useOutsideClick({
      ref: wrapperRef,
      handler: () => {
         if (searchTerm === '') setExpanded(false);
      },
   });

   const downloadExcel = useCallback(() => {
      if (!salesReport || salesReport.length === 0) {
         alert('No data available to export');
         return;
      }

      try {
         const wb = XLSX.utils.book_new();

         // Enhanced date parser that handles multiple formats
         const parseDate = (dateValue) => {
            if (!dateValue) return null;

            // If it's already a proper Date object
            if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
               return dateValue;
            }

            // If it's a timestamp (number or string representation)
            if (!isNaN(dateValue)) {
               const timestamp = Number(dateValue);
               // Check if it's milliseconds (13 digits) or seconds (10 digits)
               const date = timestamp > 9999999999 ? new Date(timestamp) : new Date(timestamp * 1000);
               if (!isNaN(date.getTime())) return date;
            }

            // If it's a string in ISO format (e.g., "2025-07-31T00:00:00.000Z")
            if (typeof dateValue === 'string' && dateValue.includes('T')) {
               const date = new Date(dateValue);
               if (!isNaN(date.getTime())) return date;
            }

            // If it's a string in "YYYY-MM-DD" format
            if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
               const [year, month, day] = dateValue.split('-');
               const date = new Date(year, month - 1, day);
               if (!isNaN(date.getTime())) return date;
            }

            // If it's a string in "DD/MM/YYYY" format
            if (typeof dateValue === 'string' && dateValue.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
               const [day, month, year] = dateValue.split('/');
               const date = new Date(year, month - 1, day);
               if (!isNaN(date.getTime())) return date;
            }

            console.warn('Could not parse date:', dateValue);
            return null;
         };

         // Format date for display
         const formatDateStrict = (date) => {
            if (!date) return '-';
            if (typeof date === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(date)) {
               return date;
            }
            const parsedDate = parseDate(date);
            if (parsedDate) {
               return parsedDate
                  .toLocaleDateString('en-GB', {
                     day: '2-digit',
                     month: '2-digit',
                     year: 'numeric',
                  })
                  .replace(/\//g, '-');
            }
            return '-';
         };

         // Check if we're using dynamic format
         const isDynamicFormat = dynamicValues && dynamicValues.includes(reportType);
         const selectedFormat = isDynamicFormat ? dynamicOptions?.find((opt) => opt.value === reportType) : null;
         const formatDefName = selectedFormat?.formatData?.defName;

         // Get columns based on format type
         const allColumns = getColumnsReportsSalesReturnDetail
            ? getColumnsReportsSalesReturnDetail(formatType, reportType)
            : dynamicColumns;

         const visibleColumns = allColumns.filter((col) => {
            if (columnVisibility && columnVisibility[col.id] === false) return false;
            if (col.isShow === false) return false;
            return true;
         });

         const sortedColumns = visibleColumns.sort((a, b) => {
            const orderA = a.orderNo || 0;
            const orderB = b.orderNo || 0;
            return orderA - orderB;
         });

         // Enhanced serial number extraction
         const getSerialNumber = (transaction, itemIndex = 0, isMultiItemMode = false) => {
            try {
               const serialFields = ['serialNo', 'serial_no', 'serialNumber', 'serial_number', 'serials', 'serialNos'];

               let serialValue = null;

               // For multi-item mode, get serial from specific item
               if (isMultiItemMode && transaction.saleLineItems && transaction.saleLineItems[itemIndex]) {
                  const item = transaction.saleLineItems[itemIndex];
                  for (const field of serialFields) {
                     if (item[field] !== undefined && item[field] !== null) {
                        serialValue = item[field];
                        break;
                     }
                  }
               }

               // Fallback to transaction level or first item
               if (!serialValue) {
                  for (const field of serialFields) {
                     if (transaction[field] !== undefined && transaction[field] !== null) {
                        serialValue = transaction[field];
                        break;
                     }
                  }

                  if (!serialValue && transaction.saleLineItems && Array.isArray(transaction.saleLineItems)) {
                     for (const lineItem of transaction.saleLineItems) {
                        for (const field of serialFields) {
                           if (lineItem[field] !== undefined && lineItem[field] !== null) {
                              serialValue = lineItem[field];
                              break;
                           }
                        }
                        if (serialValue) break;
                     }
                  }
               }

               if (serialValue !== null && serialValue !== undefined) {
                  if (Array.isArray(serialValue)) {
                     const filteredSerials = serialValue.filter((s) => s !== null && s !== undefined && s !== '');
                     return filteredSerials.length > 0 ? filteredSerials.join(', ') : 'No Serial';
                  } else if (typeof serialValue === 'string' || typeof serialValue === 'number') {
                     const serialStr = String(serialValue).trim();
                     return serialStr && serialStr !== '' ? serialStr : 'No Serial';
                  }
               }

               return 'No Serial';
            } catch (error) {
               console.error('Error getting serial number:', error);
               return 'Error';
            }
         };

         // Enhanced cell value extraction
         const getCellValue = (transaction, column, itemIndex = 0, isMultiItemMode = false) => {
            const accessor = column.accessor || column.id;
            let value = '';

            try {
               if (column.id === 'transactionAt') {
                  if (isMultiItemMode && itemIndex > 0) {
                     return '';
                  }
                  return formatDateStrict(transaction[accessor] || transaction.date || transaction.transactionDate);
               } else if (column.id === 'itemName') {
                  if (isMultiItemMode && transaction.saleLineItems && transaction.saleLineItems[itemIndex]) {
                     return transaction.saleLineItems[itemIndex].itemName || '';
                  }
                  return (
                     transaction.itemName || transaction.item_name || transaction.saleLineItems?.[0]?.itemName || ''
                  );
               } else if (column.id === 'serialNo' || column.id === 'serial_no' || column.id === 'serialNumber') {
                  return getSerialNumber(transaction, itemIndex, isMultiItemMode);
               }
               // IMPROVED QTY HANDLING - This is the key fix
               else if (
                  ['qty', 'qty1', 'quantity', 'totalQty1'].includes(column.id) ||
                  column.id.toLowerCase().includes('qty')
               ) {
                  let qtyValue = 0;

                  if (isMultiItemMode && transaction.saleLineItems && transaction.saleLineItems[itemIndex]) {
                     // For multi-item mode, get qty from specific item
                     const item = transaction.saleLineItems[itemIndex];
                     qtyValue = item[column.id] || item.qty || item.qty1 || item.quantity || 0;
                  } else {
                     // Try multiple field variations for qty1
                     const qtyFields = [
                        column.id, // exact match (qty1)
                        accessor, // accessor field
                        'qty1', // direct qty1
                        'qty', // fallback to qty
                        'quantity', // quantity field
                        'totalQty1', // total quantity
                        accessor.replace(/([A-Z])/g, '_$1').toLowerCase(), // snake_case version
                     ];

                     // First try transaction level
                     for (const field of qtyFields) {
                        if (
                           transaction[field] !== undefined &&
                           transaction[field] !== null &&
                           transaction[field] !== ''
                        ) {
                           qtyValue = transaction[field];
                           break;
                        }
                     }

                     // If not found and we have line items, try first line item
                     if (
                        !qtyValue &&
                        transaction.saleLineItems &&
                        Array.isArray(transaction.saleLineItems) &&
                        transaction.saleLineItems.length > 0
                     ) {
                        const firstItem = transaction.saleLineItems[0];
                        for (const field of qtyFields) {
                           if (firstItem[field] !== undefined && firstItem[field] !== null && firstItem[field] !== '') {
                              qtyValue = firstItem[field];
                              break;
                           }
                        }
                     }
                  }

                  const numValue = Number(qtyValue);
                  return isNaN(numValue) ? 0 : numValue;
               } else if (column.id === 'itemRate') {
                  if (isMultiItemMode && transaction.saleLineItems && transaction.saleLineItems[itemIndex]) {
                     return Number(transaction.saleLineItems[itemIndex].itemRate || 0);
                  }
                  return Number(transaction.saleLineItems?.[0]?.itemRate || 0);
               } else if (
                  column.isNumeric ||
                  ['netAmount', 'basicAmount', 'billAmount', 'taxableAmount', 'gstAmount'].includes(column.id)
               ) {
                  if (isMultiItemMode && itemIndex > 0) {
                     return '';
                  }
                  const possibleFields = [accessor, column.id, accessor.replace(/([A-Z])/g, '_$1').toLowerCase()];
                  for (const field of possibleFields) {
                     if (transaction[field] !== undefined && transaction[field] !== null) {
                        return Number(transaction[field]) || 0;
                     }
                  }
                  return 0;
               } else {
                  const nonItemColumns = ['docno', 'accountname', 'mobile', 'daybookname', 'brokername', 'notes'];
                  if (isMultiItemMode && itemIndex > 0 && nonItemColumns.includes(column.id)) {
                     return '';
                  }
                  const possibleFields = [accessor, column.id, accessor.replace(/([A-Z])/g, '_$1').toLowerCase()];
                  for (const field of possibleFields) {
                     if (transaction[field] !== undefined && transaction[field] !== null) {
                        return String(transaction[field]);
                     }
                  }
               }
            } catch (error) {
               console.error('Error getting cell value:', error);
            }

            return value || '';
         };

         // Extract all transactions from company data
         const extractAllTransactions = (companyData) => {
            const transactions = [];

            const findTransactions = (obj, path = '') => {
               if (!obj || typeof obj !== 'object') return;

               if (Array.isArray(obj)) {
                  obj.forEach((item, index) => {
                     if (
                        item &&
                        typeof item === 'object' &&
                        (item.billNo ||
                           item.transactionAt ||
                           item.accountName ||
                           item.netAmount ||
                           item.itemName ||
                           item.basicAmount ||
                           item.partyName ||
                           item.brokerName)
                     ) {
                        transactions.push(item);
                     } else {
                        findTransactions(item, `${path}[${index}]`);
                     }
                  });
               } else {
                  Object.keys(obj).forEach((key) => {
                     findTransactions(obj[key], `${path}.${key}`);
                  });
               }
            };

            findTransactions(companyData.data);
            return transactions;
         };

         // Create Excel cell with proper formatting
         const createExcelCell = (value, column, isHeader = false) => {
            if (isHeader) {
               return {
                  v: column.header || column.id,
                  t: 's',
                  s: {
                     font: { bold: true, color: { rgb: column.color ? column.color.replace('#', '') : '000000' } },
                     fill: { fgColor: { rgb: 'D3D3D3' } },
                     alignment: { horizontal: 'center' },
                  },
               };
            }

            // Handle date formatting
            if (column.id === 'transactionAt' && value && value !== '-') {
               const date = parseDate(value);
               if (date) {
                  return {
                     v: date,
                     t: 'd',
                     s: { numFmt: 'dd-mm-yyyy' },
                  };
               }
            }

            // Handle numeric values
            if (
               column.isNumeric ||
               ['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount', 'itemRate', 'qty', 'totalQty1'].includes(
                  column.id,
               )
            ) {
               const numValue = Number(value);
               if (!isNaN(numValue)) {
                  return {
                     v: numValue,
                     t: 'n',
                     s: {
                        numFmt: column.id.toLowerCase().includes('qty') ? '#,##0' : '#,##0.00',
                        font: { color: { rgb: column.color ? column.color.replace('#', '') : '000000' } },
                     },
                  };
               }
            }

            // Default string formatting
            return {
               v: value || '',
               t: 's',
               s: {
                  font: { color: { rgb: column.color ? column.color.replace('#', '') : '000000' } },
               },
            };
         };

         // Process each company's data
         salesReport.forEach((companyData) => {
            if (!companyData) return;

            const companyName = companyData.companyName || `Company_${Math.random().toString(36).substring(2, 7)}`;
            const wsData = [];
            const merges = [];
            let currentRow = 0;

            // Determine if we should use dynamic or static processing
            if (isDynamicFormat) {
               // DYNAMIC FORMAT PROCESSING
               const allTransactions = extractAllTransactions(companyData);

               // Add company header
               const companyTotal = allTransactions.reduce((sum, trans) => {
                  const amount = getCellValue(trans, { id: 'netAmount', isNumeric: true });
                  return sum + (Number(amount) || 0);
               }, 0);

               const totalQty = allTransactions.reduce((sum, trans) => {
                  const qty = getCellValue(trans, { id: 'totalQty1', isNumeric: true });
                  return sum + (Number(qty) || 0);
               }, 0);

               wsData.push([
                  {
                     v: `${companyName} (Qty: ${totalQty.toLocaleString()}, Total: ₹${companyTotal.toLocaleString()})`,
                     t: 's',
                     s: {
                        font: { bold: true },
                        fill: { fgColor: { rgb: 'E6E6E6' } },
                        alignment: { horizontal: 'center' },
                     },
                  },
               ]);

               merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: sortedColumns.length - 1 } });
               currentRow++;

               wsData.push([]); // Empty row
               currentRow++;

               if (formatDefName === 'SalesDetailParty') {
                  // Group by party
                  const groupedByParty = {};
                  allTransactions.forEach((trans) => {
                     const party = trans.accountname || trans.partyName || trans.party_name || 'Unknown Party';
                     if (!groupedByParty[party]) groupedByParty[party] = [];
                     groupedByParty[party].push(trans);
                  });

                  Object.entries(groupedByParty).forEach(([partyName, partyTrans]) => {
                     // Party header
                     wsData.push([
                        {
                           v: `${partyName} (${partyTrans.length} transactions)`,
                           t: 's',
                           s: {
                              font: { bold: true },
                              fill: { fgColor: { rgb: 'F5EEE6' } },
                              alignment: { horizontal: 'center' },
                           },
                        },
                     ]);
                     merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: sortedColumns.length - 1 } });
                     currentRow++;

                     // Column headers
                     wsData.push(sortedColumns.map((col) => createExcelCell(null, col, true)));
                     currentRow++;

                     // Transaction data
                     partyTrans.forEach((transaction) => {
                        if (
                           formatType === 'with_item_detail' &&
                           transaction.saleLineItems &&
                           transaction.saleLineItems.length > 1
                        ) {
                           transaction.saleLineItems.forEach((item, itemIndex) => {
                              const row = sortedColumns.map((col) =>
                                 createExcelCell(getCellValue(transaction, col, itemIndex, true), col),
                              );
                              wsData.push(row);
                              currentRow++;
                           });
                        } else {
                           const row = sortedColumns.map((col) => createExcelCell(getCellValue(transaction, col), col));
                           wsData.push(row);
                           currentRow++;
                        }
                     });

                     wsData.push([]); // Empty row after party
                     currentRow++;
                  });
               } else if (formatDefName === 'SalesDetailDate') {
                  // Group by date
                  const groupedByDate = {};
                  allTransactions.forEach((trans) => {
                     const date =
                        formatDateStrict(trans.transactionAt || trans.date || trans.transaction_date) || 'No Date';
                     if (!groupedByDate[date]) groupedByDate[date] = [];
                     groupedByDate[date].push(trans);
                  });

                  Object.entries(groupedByDate).forEach(([date, dateTrans]) => {
                     // Date header
                     wsData.push([
                        {
                           v: `${date} (${dateTrans.length} transactions)`,
                           t: 's',
                           s: {
                              font: { bold: true },
                              fill: { fgColor: { rgb: 'E5E5E5' } },
                              alignment: { horizontal: 'center' },
                           },
                        },
                     ]);
                     merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: sortedColumns.length - 1 } });
                     currentRow++;

                     // Column headers
                     wsData.push(sortedColumns.map((col) => createExcelCell(null, col, true)));
                     currentRow++;

                     // Transaction data
                     dateTrans.forEach((transaction) => {
                        if (
                           formatType === 'with_item_detail' &&
                           transaction.saleLineItems &&
                           transaction.saleLineItems.length > 1
                        ) {
                           transaction.saleLineItems.forEach((item, itemIndex) => {
                              const row = sortedColumns.map((col) =>
                                 createExcelCell(getCellValue(transaction, col, itemIndex, true), col),
                              );
                              wsData.push(row);
                              currentRow++;
                           });
                        } else {
                           const row = sortedColumns.map((col) => createExcelCell(getCellValue(transaction, col), col));
                           wsData.push(row);
                           currentRow++;
                        }
                     });

                     wsData.push([]); // Empty row after date
                     currentRow++;
                  });
               } else {
                  // Default dynamic format - just list all transactions
                  wsData.push(sortedColumns.map((col) => createExcelCell(null, col, true)));
                  currentRow++;

                  allTransactions.forEach((transaction) => {
                     if (
                        formatType === 'with_item_detail' &&
                        transaction.saleLineItems &&
                        transaction.saleLineItems.length > 1
                     ) {
                        transaction.saleLineItems.forEach((item, itemIndex) => {
                           const row = sortedColumns.map((col) =>
                              createExcelCell(getCellValue(transaction, col, itemIndex, true), col),
                           );
                           wsData.push(row);
                           currentRow++;
                        });
                     } else {
                        const row = sortedColumns.map((col) => createExcelCell(getCellValue(transaction, col), col));
                        wsData.push(row);
                        currentRow++;
                     }
                  });
               }
            } else {
               // STATIC FORMAT PROCESSING (Original logic)
               if (reportType === REPORT_TYPE.party) {
                  // Add company header
                  const companyTotal =
                     companyData.data?.reduce((sum, account) => sum + (account.totalNetAmt || 0), 0) || 0;
                  const totalQty = companyData.data?.reduce((sum, account) => sum + (account.totalQty1 || 0), 0) || 0;

                  wsData.push([
                     {
                        v: `${companyName} (Qty: ${totalQty.toLocaleString()}, Total: ₹${companyTotal.toLocaleString()})`,
                        t: 's',
                        s: {
                           font: { bold: true },
                           fill: { fgColor: { rgb: 'E6E6E6' } },
                           alignment: { horizontal: 'center' },
                        },
                     },
                  ]);
                  merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: sortedColumns.length - 1 } });
                  currentRow++;

                  wsData.push([]); // Empty row
                  currentRow++;

                  // Process each account
                  companyData.data?.forEach((accountData) => {
                     if (!accountData) return;

                     // Add account header
                     wsData.push([
                        {
                           v: `${
                              accountData.accountName
                           } (Qty: ${accountData.totalQty1.toLocaleString()}, Amount: ₹${accountData.totalNetAmt.toLocaleString()})`,
                           t: 's',
                           s: {
                              font: { bold: true },
                              fill: { fgColor: { rgb: 'F5EEE6' } },
                              alignment: { horizontal: 'center' },
                           },
                        },
                     ]);
                     merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: sortedColumns.length - 1 } });
                     currentRow++;

                     // Add column headers
                     wsData.push(sortedColumns.map((col) => createExcelCell(null, col, true)));
                     currentRow++;

                     // Add transaction data
                     accountData.data?.forEach((transaction) => {
                        if (!transaction) return;

                        if (
                           formatType === 'with_item_detail' &&
                           transaction.saleLineItems &&
                           transaction.saleLineItems.length > 1
                        ) {
                           transaction.saleLineItems.forEach((item, itemIndex) => {
                              const row = sortedColumns.map((col) =>
                                 createExcelCell(getCellValue(transaction, col, itemIndex, true), col),
                              );
                              wsData.push(row);
                              currentRow++;
                           });
                        } else {
                           const row = sortedColumns.map((col) => createExcelCell(getCellValue(transaction, col), col));
                           wsData.push(row);
                           currentRow++;
                        }
                     });

                     wsData.push([]); // Empty row after account
                     currentRow++;
                  });
               } else {
                  // For date/broker report type
                  const companyTotal =
                     companyData.data?.reduce((sum, account) => {
                        return (
                           sum + (account.data?.reduce((acc, trans) => acc + (Number(trans.netAmount) || 0), 0) || 0)
                        );
                     }, 0) || 0;

                  const totalQty = companyData.data?.reduce((sum, account) => sum + (account.totalQty1 || 0), 0) || 0;

                  wsData.push([
                     {
                        v: `${companyName} (Qty: ${totalQty.toLocaleString()}, Total: ₹${companyTotal.toLocaleString()})`,
                        t: 's',
                        s: {
                           font: { bold: true },
                           fill: { fgColor: { rgb: 'E6E6E6' } },
                           alignment: { horizontal: 'center' },
                        },
                     },
                  ]);
                  merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: sortedColumns.length - 1 } });
                  currentRow++;

                  wsData.push([]); // Empty row
                  currentRow++;

                  // Add column headers
                  wsData.push(sortedColumns.map((col) => createExcelCell(null, col, true)));
                  currentRow++;

                  // Process data
                  companyData.data?.forEach((accountData) => {
                     if (!accountData) return;

                     // Add account header if it exists
                     if (accountData.accountName) {
                        const accountTotal =
                           accountData.data?.reduce((sum, trans) => sum + (Number(trans.netAmount) || 0), 0) || 0;
                        const accountQty = accountData.totalQty1 || 0;

                        wsData.push([
                           {
                              v: `${
                                 accountData.accountName
                              } (Qty: ${accountQty.toLocaleString()}, Amount: ₹${accountTotal.toLocaleString()})`,
                              t: 's',
                              s: {
                                 font: { bold: true },
                                 fill: { fgColor: { rgb: 'F5EEE6' } },
                                 alignment: { horizontal: 'center' },
                              },
                           },
                        ]);
                        merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: sortedColumns.length - 1 } });
                        currentRow++;

                        // Re-add headers after account header
                        wsData.push(sortedColumns.map((col) => createExcelCell(null, col, true)));
                        currentRow++;
                     }

                     // Add transaction data
                     accountData.data?.forEach((transaction) => {
                        if (!transaction) return;

                        if (
                           formatType === 'with_item_detail' &&
                           transaction.saleLineItems &&
                           transaction.saleLineItems.length > 1
                        ) {
                           transaction.saleLineItems.forEach((item, itemIndex) => {
                              const row = sortedColumns.map((col) =>
                                 createExcelCell(getCellValue(transaction, col, itemIndex, true), col),
                              );
                              wsData.push(row);
                              currentRow++;
                           });
                        } else {
                           const row = sortedColumns.map((col) => createExcelCell(getCellValue(transaction, col), col));
                           wsData.push(row);
                           currentRow++;
                        }
                     });
                  });
               }
            }

            // Create worksheet
            const ws = XLSX.utils.aoa_to_sheet(wsData);

            // Apply merges
            if (merges.length > 0) {
               ws['!merges'] = merges;
            }

            // Set column widths
            const colWidths = sortedColumns.map((col) => {
               if (col.id === 'transactionAt') return { wch: 12 };
               if (['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount', 'itemRate'].includes(col.id)) {
                  return { wch: 15 };
               }
               if (col.id === 'itemName') return { wch: 25 };
               if (col.id === 'accountName' || col.id === 'accountname') return { wch: 20 };
               if (col.id === 'serialNo') return { wch: 20 };

               const maxContentLength = Math.max(
                  col.header?.length || col.id.length,
                  15, // minimum width
               );

               return { wch: Math.min(Math.max(maxContentLength, 10), 30) };
            });

            ws['!cols'] = colWidths;

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(wb, ws, companyName.substring(0, 31).replace(/[\\/*[\]:?]/g, ''));
         });

         // Generate and download the Excel file
         const formatName = isDynamicFormat
            ? dynamicOptions?.find((opt) => opt.value === reportType)?.label || reportType
            : REPORT_TYPE_OPTIONS?.find((opt) => opt.value === reportType)?.label || reportType;

         const fileName = `${formatName}_Detail_Report_${moment().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`;
         XLSX.writeFile(wb, fileName);
      } catch (error) {
         console.error('Error generating Excel file:', error);
         alert('Error generating Excel file. Please check console for details.');
      }
   }, [
      salesReport,
      dynamicValues,
      reportType,
      dynamicOptions,
      getColumnsReportsSalesReturnDetail,
      formatType,
      dynamicColumns,
      columnVisibility,
   ]);

   const downloadPDF = useCallback(() => {
      if (!salesReport || salesReport.length === 0) {
         alert('No data available to export');
         return;
      }

      const doc = new jsPDF({
         orientation: 'landscape',
         unit: 'mm',
      });

      const formatDateStrict = (date) => {
         if (!date) return '-';
         if (typeof date === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(date)) {
            return date;
         }
         const m = moment(date);
         return m.isValid() ? m.format('DD-MM-YYYY') : '-';
      };

      // Color conversion utility
      const convertBackendColor = (backendColor) => {
         if (!backendColor) return [0, 0, 0]; // Default black
         if (backendColor.startsWith('#')) {
            // Convert hex to RGB array for jsPDF
            const hex = backendColor.substring(1);
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            return [r, g, b];
         }
         if (backendColor.startsWith('0xff')) {
            const hex = backendColor.substring(4);
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            return [r, g, b];
         }
         if (/^[0-9A-Fa-f]{6}$/.test(backendColor)) {
            const r = parseInt(backendColor.substring(0, 2), 16);
            const g = parseInt(backendColor.substring(2, 4), 16);
            const b = parseInt(backendColor.substring(4, 6), 16);
            return [r, g, b];
         }
         return [0, 0, 0]; // Default black
      };

      // Add title
      const title = `Sale Detail Report (${formatDateStrict(dateRange.fromDate)} to ${formatDateStrict(
         dateRange.toDate,
      )})`;
      doc.setFontSize(14);

      // Calculate center position for title
      const pdfWidth = doc.internal.pageSize.getWidth();
      const titleWidth = (doc.getStringUnitWidth(title) * doc.internal.getFontSize()) / doc.internal.scaleFactor;
      const titleXPosition = (pdfWidth - titleWidth) / 2;
      doc.text(title, titleXPosition, 10);

      // Add company filter info
      if (companyFilter && companyFilter.length > 0) {
         const companyNames = companyFilter
            .map((id) => {
               const company = companyList?.find((c) => c.id === id);
               return company ? company.name : '';
            })
            .filter((name) => name !== '')
            .join(', ');

         if (companyNames) {
            const companyText = `Companies: ${companyNames}`;
            doc.setFontSize(8);
            const companyWidth =
               (doc.getStringUnitWidth(companyText) * doc.internal.getFontSize()) / doc.internal.scaleFactor;
            const companyXPosition = (pdfWidth - companyWidth) / 2;
            doc.text(companyText, companyXPosition, 15);
         }
      }

      // Add report type info
      const reportTypeText = `Report Type: ${
         REPORT_TYPE_OPTIONS?.find((opt) => opt.value === reportType)?.label || reportType
      }`;
      doc.setFontSize(8);
      const reportTypeWidth =
         (doc.getStringUnitWidth(reportTypeText) * doc.internal.getFontSize()) / doc.internal.scaleFactor;
      const reportTypeXPosition = (pdfWidth - reportTypeWidth) / 2;
      doc.text(reportTypeText, reportTypeXPosition, 20);

      // Check format type
      const isDynamicFormat = dynamicValues && dynamicValues.includes(reportType);
      const selectedFormat = isDynamicFormat ? dynamicOptions?.find((opt) => opt.value === reportType) : null;
      const formatDefName = selectedFormat?.formatData?.defName;

      // Check if this is a date-based report
      const isDateBasedReport =
         reportType === REPORT_TYPE.date || reportType === 'date' || formatDefName === 'SalesDetailDate';

      // Get columns
      const allColumns = getColumnsReportsSalesReturnDetail
         ? getColumnsReportsSalesReturnDetail(formatType, reportType)
         : dynamicColumns;

      const visibleColumns = allColumns.filter((col) => {
         if (columnVisibility && columnVisibility[col.id] === false) return false;
         if (col.isShow === false) return false;
         // FILTER OUT DATE COLUMN IN DATE-BASED REPORTS
         if (isDateBasedReport && (col.id === 'transactionAt' || col.id === 'date' || col.id === 'transaction_at')) {
            return false;
         }
         return true;
      });

      const sortedColumns = visibleColumns.sort((a, b) => {
         const orderA = a.orderNo || 0;
         const orderB = b.orderNo || 0;
         return orderA - orderB;
      });

      // FIXED: More balanced column width calculation
      const totalFlex = sortedColumns.reduce((sum, col) => sum + (col.flex || 1), 0);
      const pageWidth = doc.internal.pageSize.getWidth() - 20; // Available width

      const calculateColumnWidth = (column) => {
         const baseFlex = column.flex || 1;
         const baseWidth = (pageWidth * baseFlex) / totalFlex;

         if (!isDynamicFormat) {
            // Priority 1: Party/Account name columns
            const isPartyColumn = column.id === 'accountname' || column.id === 'accountName';
            if (isPartyColumn) {
               return Math.max(baseWidth * 1.2, 25); // Moderate increase, minimum 25mm
            }

            // Priority 2: Item name columns
            const isItemColumn = column.id === 'itemName';
            if (isItemColumn) {
               return Math.max(baseWidth * 1.1, 20); // Moderate increase, minimum 20mm
            }

            // Priority 3: Serial number columns - REDUCED from excessive width
            const isSerialColumn =
               column.id === 'serialNo' || column.id === 'serial_no' || column.id === 'serialNumber';
            if (isSerialColumn) {
               return Math.max(baseWidth * 1.3, 22); // Reduced from 2.0 to 1.3, minimum 22mm
            }

            // Priority 4: Rate columns - FIXED spacing
            const isRateColumn =
               column.id === 'itemRate' ||
               column.id === 'rate' ||
               (column.isNumeric &&
                  (column.header?.toLowerCase().includes('rate') || column.header?.toLowerCase().includes('price')));
            if (isRateColumn) {
               return Math.max(baseWidth * 0.9, 18); // Consistent rate column width
            }

            // Priority 5: Daybook columns - FIXED spacing
            const isDaybookColumn = column.id === 'daybookname' || column.id === 'daybook';
            if (isDaybookColumn) {
               return Math.max(baseWidth * 1.0, 20); // Standard daybook width
            }

            // Priority 6: Amount columns - ENSURE bill amount isn't cut off
            const isAmountColumn =
               column.id === 'netAmount' ||
               column.id === 'billAmount' ||
               column.id === 'basicAmount' ||
               column.id === 'bill' ||
               (column.isNumeric && column.header?.toLowerCase().includes('amount'));
            if (isAmountColumn) {
               return Math.max(baseWidth * 0.95, 20); // Ensure adequate space for amounts
            }

            // Priority 7: Quantity columns
            const isQuantityColumn =
               column.id === 'qty1' || column.id === 'qty' || column.header?.toLowerCase().includes('qty');
            if (isQuantityColumn) {
               return Math.max(baseWidth * 0.8, 15); // Compact but readable
            }

            // Priority 8: Document number - can be compressed
            const isDocColumn = column.id === 'docno' || column.id === 'docNo';
            if (isDocColumn) {
               return Math.max(baseWidth * 0.7, 12); // More compressed
            }

            // Priority 9: Mobile/Phone columns
            const isMobileColumn = column.id === 'mobile' || column.id === 'phone';
            if (isMobileColumn) {
               return Math.max(baseWidth * 0.9, 18);
            }
         }

         // Default calculation - ensure minimum width
         return Math.max(baseWidth, 10); // Minimum 10mm for any column
      };

      // FIXED: Better column styles with proper spacing
      const columnStyles = {};
      sortedColumns.forEach((col, index) => {
         const textColor = col.color ? convertBackendColor(col.color) : [0, 0, 0];
         const fontStyle =
            col.isBold && col.isItalic ? 'bolditalic' : col.isBold ? 'bold' : col.isItalic ? 'italic' : 'normal';

         const isSerialColumn = col.id === 'serialNo' || col.id === 'serial_no' || col.id === 'serialNumber';
         const isAmountColumn =
            col.id === 'netAmount' || col.id === 'billAmount' || col.id === 'basicAmount' || col.id === 'bill';

         columnStyles[index] = {
            cellWidth: calculateColumnWidth(col),
            halign: col.isNumeric ? 'right' : 'left',
            cellPadding: isSerialColumn ? 1.5 : 1, // Consistent padding
            textColor: textColor,
            fontStyle: fontStyle,
            fontSize: isSerialColumn ? 6 : 8, // Smaller font for serial numbers only
            overflow: 'linebreak',
            valign: 'top',
            minCellWidth: isAmountColumn ? 18 : isSerialColumn ? 20 : 8, // Ensure amount columns have minimum width
         };
      });

      // ENHANCED SERIAL NUMBER HANDLING
      const getSerialNumber = (transaction) => {
         try {
            const serialFields = [
               'serialNo',
               'serial_no',
               'serialNumber',
               'serial_number',
               'serials',
               'serialNos',
               'serialNumbers',
               'serialData',
            ];

            let serialValue = null;

            for (const field of serialFields) {
               if (transaction[field] !== undefined && transaction[field] !== null) {
                  serialValue = transaction[field];
                  break;
               }
            }

            if (!serialValue && transaction.saleLineItems && Array.isArray(transaction.saleLineItems)) {
               for (const lineItem of transaction.saleLineItems) {
                  for (const field of serialFields) {
                     if (lineItem[field] !== undefined && lineItem[field] !== null) {
                        serialValue = lineItem[field];
                        break;
                     }
                  }
                  if (serialValue) break;
               }
            }

            if (serialValue !== null && serialValue !== undefined) {
               if (Array.isArray(serialValue)) {
                  const filteredSerials = serialValue.filter((s) => s !== null && s !== undefined && s !== '');
                  if (filteredSerials.length > 0) {
                     return filteredSerials.join('\n');
                  }
               } else if (typeof serialValue === 'string' || typeof serialValue === 'number') {
                  const serialStr = String(serialValue).trim();
                  if (serialStr && serialStr !== '' && serialStr !== 'null' && serialStr !== 'undefined') {
                     if (serialStr.includes(',')) {
                        return serialStr
                           .split(',')
                           .map((s) => s.trim())
                           .filter((s) => s)
                           .join('\n');
                     }
                     return serialStr;
                  }
               }
            }

            return '-'; // Return dash instead of "No Serial" for cleaner look
         } catch (error) {
            console.error('Error getting serial number:', error);
            return '-';
         }
      };

      // FIXED: Enhanced cell value function with better data handling
      const getCellValue = (transaction, column, itemIndex = 0, isMultiItemMode = false) => {
         const accessor = column.accessor || column.id;
         let value = '';

         try {
            if (column.id === 'transactionAt') {
               if (isMultiItemMode && itemIndex > 0) return '';
               return formatDateStrict(transaction[accessor] || transaction.date || transaction.transactionDate);
            } else if (column.id === 'itemName') {
               if (isMultiItemMode && transaction.saleLineItems && transaction.saleLineItems[itemIndex]) {
                  return transaction.saleLineItems[itemIndex].itemName || '';
               }
               return transaction.itemName || transaction.item_name || transaction.saleLineItems?.[0]?.itemName || '';
            } else if (column.id === 'serialNo' || column.id === 'serial_no' || column.id === 'serialNumber') {
               return getSerialNumber(transaction, itemIndex, isMultiItemMode);
            }
            // FIXED: Rate column handling
            else if (column.id === 'itemRate' || column.id === 'rate') {
               let rateValue = 0;
               if (isMultiItemMode && transaction.saleLineItems && transaction.saleLineItems[itemIndex]) {
                  rateValue =
                     transaction.saleLineItems[itemIndex].itemRate || transaction.saleLineItems[itemIndex].rate || 0;
               } else {
                  rateValue = transaction.itemRate || transaction.rate || transaction.saleLineItems?.[0]?.itemRate || 0;
               }
               const numValue = Number(rateValue);
               return isNaN(numValue) ? 0 : numValue;
            }
            // FIXED: Daybook handling
            else if (column.id === 'daybookname' || column.id === 'daybook') {
               if (isMultiItemMode && itemIndex > 0) return '';
               return transaction.daybookname || transaction.daybook || transaction.daybookName || '-';
            }
            // IMPROVED QTY HANDLING
            else if (
               ['qty', 'qty1', 'quantity', 'totalQty1'].includes(column.id) ||
               column.id.toLowerCase().includes('qty')
            ) {
               let qtyValue = 0;
               if (isMultiItemMode && transaction.saleLineItems && transaction.saleLineItems[itemIndex]) {
                  const item = transaction.saleLineItems[itemIndex];
                  qtyValue = item[column.id] || item.qty || item.qty1 || item.quantity || 0;
               } else {
                  const qtyFields = [column.id, accessor, 'qty1', 'qty', 'quantity', 'totalQty1'];
                  for (const field of qtyFields) {
                     if (transaction[field] !== undefined && transaction[field] !== null && transaction[field] !== '') {
                        qtyValue = transaction[field];
                        break;
                     }
                  }
                  if (
                     !qtyValue &&
                     transaction.saleLineItems &&
                     Array.isArray(transaction.saleLineItems) &&
                     transaction.saleLineItems.length > 0
                  ) {
                     const firstItem = transaction.saleLineItems[0];
                     for (const field of qtyFields) {
                        if (firstItem[field] !== undefined && firstItem[field] !== null && firstItem[field] !== '') {
                           qtyValue = firstItem[field];
                           break;
                        }
                     }
                  }
               }
               const numValue = Number(qtyValue);
               return isNaN(numValue) ? 0 : numValue;
            }
            // FIXED: Amount columns handling
            else if (
               column.isNumeric ||
               ['netAmount', 'basicAmount', 'billAmount', 'taxableAmount', 'gstAmount', 'bill'].includes(column.id)
            ) {
               if (isMultiItemMode && itemIndex > 0) return '';
               const possibleFields = [accessor, column.id, accessor.replace(/([A-Z])/g, '_$1').toLowerCase()];
               for (const field of possibleFields) {
                  if (transaction[field] !== undefined && transaction[field] !== null) {
                     const numValue = Number(transaction[field]);
                     return isNaN(numValue) ? 0 : numValue;
                  }
               }
               return 0;
            } else {
               // Handle other text columns
               const nonItemColumns = ['docno', 'accountname', 'mobile', 'daybookname', 'brokername', 'notes'];
               if (isMultiItemMode && itemIndex > 0 && nonItemColumns.includes(column.id)) {
                  return '';
               }
               const possibleFields = [accessor, column.id, accessor.replace(/([A-Z])/g, '_$1').toLowerCase()];
               for (const field of possibleFields) {
                  if (transaction[field] !== undefined && transaction[field] !== null) {
                     const strValue = String(transaction[field]).trim();
                     return strValue === 'null' || strValue === 'undefined' ? '-' : strValue;
                  }
               }
            }
         } catch (error) {
            console.error('Error getting cell value:', error);
         }

         return value || '-';
      };

      // Enhanced transaction rows creation
      const createTransactionRows = (transactions, isMultiItemFormat = false) => {
         const rows = [];

         transactions.forEach((transaction, transIndex) => {
            const hasMultipleItems =
               isMultiItemFormat &&
               transaction.saleLineItems &&
               Array.isArray(transaction.saleLineItems) &&
               transaction.saleLineItems.length > 1;

            if (hasMultipleItems) {
               transaction.saleLineItems.forEach((item, itemIndex) => {
                  const isFirstItem = itemIndex === 0;
                  const isLastItem = itemIndex === transaction.saleLineItems.length - 1;

                  const row = sortedColumns.map((col, colIndex) => {
                     const textColor = col.color ? convertBackendColor(col.color) : [0, 0, 0];
                     const fontStyle =
                        col.isBold && col.isItalic
                           ? 'bolditalic'
                           : col.isBold
                           ? 'bold'
                           : col.isItalic
                           ? 'italic'
                           : 'normal';

                     const isSerialColumn =
                        col.id === 'serialNo' || col.id === 'serial_no' || col.id === 'serialNumber';

                     return {
                        content: getCellValue(transaction, col, itemIndex, true),
                        styles: {
                           fontSize: isSerialColumn ? 6 : 8,
                           textColor: textColor,
                           fontStyle: fontStyle,
                           halign: col.isNumeric ? 'right' : 'left',
                           valign: 'top',
                           cellPadding: isSerialColumn ? 2 : 0.5,
                           fillColor: isFirstItem ? null : '#F8FAFC',
                           lineWidth: { bottom: isLastItem ? 0.3 : 0.1 },
                           lineColor: [200, 200, 200],
                           overflow: 'linebreak',
                        },
                     };
                  });
                  rows.push(row);
               });
            } else {
               const row = sortedColumns.map((col, colIndex) => {
                  const textColor = col.color ? convertBackendColor(col.color) : [0, 0, 0];
                  const fontStyle =
                     col.isBold && col.isItalic
                        ? 'bolditalic'
                        : col.isBold
                        ? 'bold'
                        : col.isItalic
                        ? 'italic'
                        : 'normal';

                  const isSerialColumn = col.id === 'serialNo' || col.id === 'serial_no' || col.id === 'serialNumber';

                  return {
                     content: getCellValue(transaction, col, 0, false),
                     styles: {
                        fontSize: isSerialColumn ? 6 : 8,
                        textColor: textColor,
                        fontStyle: fontStyle,
                        halign: col.isNumeric ? 'right' : 'left',
                        valign: 'top',
                        cellPadding: isSerialColumn ? 2 : 0.5,
                        lineWidth: { bottom: 0.2 },
                        lineColor: [200, 200, 200],
                        overflow: 'linebreak',
                     },
                  };
               });
               rows.push(row);
            }
         });

         return rows;
      };

      // Enhanced header creation
      const createDynamicHeader = () => {
         return sortedColumns.map((col) => {
            const textColor = col.color ? convertBackendColor(col.color) : [0, 0, 0];
            const fontStyle =
               col.isBold && col.isItalic ? 'bolditalic' : col.isBold ? 'bold' : col.isItalic ? 'italic' : 'normal';

            return {
               content: col.header || col.id,
               styles: {
                  fontSize: 7,
                  fontStyle: fontStyle,
                  textColor: textColor,
                  halign: col.isNumeric ? 'right' : 'left',
                  cellPadding: 1,
                  lineWidth: { bottom: 0.4 },
                  lineColor: [100, 100, 100],
               },
            };
         });
      };

      // Process data with improved logic
      let data = [];
      const totalCols = sortedColumns.length;

      const addStyledRow = (content, colSpan = 1, styles = {}) => {
         const row = Array(totalCols).fill({ content: '', styles: {} });
         row[0] = {
            content,
            colSpan: Math.min(colSpan, totalCols),
            styles: {
               fillColor: styles.fillColor || null,
               fontStyle: styles.fontStyle || 'normal',
               textColor: styles.textColor || [0, 0, 0],
               fontSize: styles.fontSize || 8,
               cellPadding: styles.cellPadding || 1,
               halign: styles.halign || 'left',
               lineWidth: { bottom: 0.3 },
               lineColor: [150, 150, 150],
            },
         };
         data.push(row);
      };

      const addTransactionRows = (transactions) => {
         const transactionRows = createTransactionRows(transactions, formatType === 'with_item_detail');
         data.push(...transactionRows);
      };

      const calculateTotals = (transactions) => {
         const totals = {};
         sortedColumns.forEach((col) => {
            if (col.isNumeric || ['netAmount', 'basicAmount', 'totalQty1', 'billAmount'].includes(col.id)) {
               totals[col.id] = transactions.reduce((sum, trans) => {
                  const val = getCellValue(trans, col);
                  const numVal = Number(String(val).replace(/[₹,]/g, '')) || 0;
                  return sum + numVal;
               }, 0);
            }
         });
         return totals;
      };

      // Main processing logic (same as before)
      salesReport.forEach((companyData) => {
         const companyName = companyData.companyName;
         let allTransactions = [];

         if (isDynamicFormat) {
            allTransactions = companyData.data || [];
         } else {
            if (reportType === REPORT_TYPE.party || reportType === 'party') {
               companyData.data?.forEach((accountData) => {
                  if (accountData.data) {
                     allTransactions = allTransactions.concat(accountData.data);
                  }
               });
            } else {
               companyData.data?.forEach((dateData) => {
                  if (dateData.data) {
                     allTransactions = allTransactions.concat(dateData.data);
                  }
               });
            }
         }

         const companyTotals = calculateTotals(allTransactions);
         const qtyCol = sortedColumns.find(
            (col) => col.id === 'totalQty1' || col.id === 'qty' || col.header?.toLowerCase().includes('qty'),
         );
         const amountCol = sortedColumns.find(
            (col) =>
               ['netAmount', 'billAmount', 'basicAmount'].includes(col.id) ||
               col.header?.toLowerCase().includes('amount'),
         );

         const totalQty = qtyCol ? companyTotals[qtyCol.id] || 0 : 0;
         const totalAmount = amountCol ? companyTotals[amountCol.id] || 0 : 0;

         addStyledRow(
            `${companyName} (${
               allTransactions.length
            } transactions) Qty: ${totalQty.toLocaleString()} Amt: ${totalAmount.toLocaleString()}`,
            totalCols,
            { fillColor: '#F1F0E8', fontStyle: 'bold', fontSize: 9, cellPadding: 2 },
         );

         if (isDynamicFormat) {
            if (formatDefName === 'SalesDetailParty') {
               const groupedByParty = {};
               allTransactions.forEach((trans) => {
                  const party = trans.accountname || trans.partyName || trans.party_name || 'Unknown Party';
                  if (!groupedByParty[party]) groupedByParty[party] = [];
                  groupedByParty[party].push(trans);
               });

               Object.entries(groupedByParty).forEach(([partyName, partyTrans]) => {
                  const partyTotals = calculateTotals(partyTrans);
                  const partyQty = qtyCol ? partyTotals[qtyCol.id] || 0 : 0;
                  const partyAmount = amountCol ? partyTotals[amountCol.id] || 0 : 0;

                  addStyledRow(
                     `${partyName} Qty: ${partyQty.toLocaleString()} Amt: ${partyAmount.toLocaleString()}`,
                     totalCols,
                     { fillColor: '#F5EEE6', fontStyle: 'bold', fontSize: 8, cellPadding: 1 },
                  );

                  addTransactionRows(partyTrans);
               });
            } else if (formatDefName === 'SalesDetailDate') {
               const groupedByDate = {};
               allTransactions.forEach((trans) => {
                  const date =
                     formatDateStrict(trans.transactionAt || trans.date || trans.transaction_date) || 'No Date';
                  if (!groupedByDate[date]) groupedByDate[date] = [];
                  groupedByDate[date].push(trans);
               });

               Object.entries(groupedByDate).forEach(([date, dateTrans]) => {
                  const dateTotals = calculateTotals(dateTrans);
                  const dateQty = qtyCol ? dateTotals[qtyCol.id] || 0 : 0;
                  const dateAmount = amountCol ? dateTotals[amountCol.id] || 0 : 0;

                  addStyledRow(
                     `${date} Qty: ${dateQty.toLocaleString()} Amt: ${dateAmount.toLocaleString()}`,
                     totalCols,
                     { fillColor: '#E5E5E5', fontStyle: 'bold', fontSize: 8, cellPadding: 1 },
                  );

                  addTransactionRows(dateTrans);
               });
            } else {
               addTransactionRows(allTransactions);
            }
         } else {
            if (reportType === REPORT_TYPE.party || reportType === 'party') {
               if (companyData.data) {
                  companyData.data.forEach((accountData) => {
                     const accountTransactions = accountData.data || [];
                     const accountTotals = calculateTotals(accountTransactions);
                     const accountQty = qtyCol ? accountTotals[qtyCol.id] || 0 : 0;
                     const accountAmount = amountCol ? accountTotals[amountCol.id] || 0 : 0;

                     addStyledRow(
                        `${
                           accountData.accountName
                        } Qty: ${accountQty.toLocaleString()} Amt: ${accountAmount.toLocaleString()}`,
                        totalCols,
                        { fillColor: '#F5EEE6', fontStyle: 'bold', fontSize: 8, cellPadding: 1 },
                     );

                     addTransactionRows(accountTransactions);
                  });
               }
            } else {
               const itemData = companyData.data?.flatMap((item) => item.data || []) || [];
               const groupedByDate = {};
               itemData.forEach((trans) => {
                  const date = formatDateStrict(trans.transactionAt) || 'No Date';
                  if (!groupedByDate[date]) groupedByDate[date] = [];
                  groupedByDate[date].push(trans);
               });

               Object.entries(groupedByDate).forEach(([date, dateTransactions]) => {
                  const dateTotals = calculateTotals(dateTransactions);
                  const dateQty = qtyCol ? dateTotals[qtyCol.id] || 0 : 0;
                  const dateAmount = amountCol ? dateTotals[amountCol.id] || 0 : 0;

                  addStyledRow(
                     `${date} Qty: ${dateQty.toLocaleString()} Amt: ${dateAmount.toLocaleString()}`,
                     totalCols,
                     { fillColor: '#E5E5E5', fontStyle: 'bold', fontSize: 8, cellPadding: 1 },
                  );

                  addTransactionRows(dateTransactions);
               });
            }
         }

         data.push(
            Array(totalCols).fill({
               content: '',
               styles: {
                  cellPadding: 2,
                  lineWidth: { bottom: 0.3 },
                  lineColor: [220, 220, 220],
               },
            }),
         );
      });

      if (data.length === 0) {
         alert('No data found to generate PDF');
         return;
      }

      // FIXED: Better table configuration to prevent cutoffs
      doc.autoTable({
         head: [createDynamicHeader()],
         body: data,
         startY: 25,
         margin: { left: 10, right: 10 },
         styles: {
            fontSize: 8,
            cellPadding: 1,
            overflow: 'linebreak',
            lineWidth: 0,
            valign: 'top',
         },
         headStyles: {
            fillColor: [220, 220, 220],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            halign: 'center',
            fontSize: 7,
            lineWidth: { bottom: 0.4 },
            lineColor: [100, 100, 100],
         },
         bodyStyles: {
            lineWidth: 0,
            valign: 'top',
         },
         alternateRowStyles: {
            fillColor: '#F8FAFC',
            lineWidth: 0,
         },
         columnStyles: columnStyles,
         tableWidth: 'wrap',
         rowPageBreak: 'avoid',
         pageBreak: 'auto',
         showHead: 'everyPage',
         horizontalPageBreak: true,
         horizontalPageBreakRepeat: 0,
      });

      const formatName = isDynamicFormat
         ? dynamicOptions?.find((opt) => opt.value === reportType)?.label || reportType
         : REPORT_TYPE_OPTIONS?.find((opt) => opt.value === reportType)?.label || reportType;

      doc.save(`${formatName}_Report_${moment().format('YYYYMMDD_HHmmss')}.pdf`);
   }, [
      salesReport,
      companyFilter,
      dateRange,
      reportType,
      dynamicColumns,
      columnVisibility,
      companyList,
      formatType,
      dynamicOptions,
      dynamicValues,
   ]);

   const deletePurchaseReturn = () => {
      deleteRecord(viewDetail?.id, deleteSaleInvoice, dispatch, user?.loginhistory?.companyId, userGlobalSettingsData);
      setIsOpenPurchaseReturnSingleView(false);
   };

   const handleSalesReturnDetailsClose = () => {
      setIsOpenPurchaseReturnSingleView();
   };

   // Render functions
   const renderDataRows = useCallback(
      (itemData, isPartyReport = false, nestingLevel = 0) => {
         if (!itemData || !Array.isArray(itemData)) return null;

         const paddingLeft = isPartyReport ? `${(nestingLevel + 1) * 20}px` : '4px';

         const handledata = async (data) => {
            const storedCompanyId = localStorage.getItem('companyId');
            const companyId = parseInt(storedCompanyId);
            setIsModalLoading(true); // Set loading state at the start

            try {
               if (data.companyId === companyId) {
                  await dispatch(
                     getSingleSalesReturnWithPrevAndNext({
                        id: data.id,
                        page: 1,
                        limit: 1,
                        sortBy: 'id',
                        sortOrder: 'desc',
                     }),
                  );
                  setIsOpenPurchaseReturnSingleView(true);
               }
               setTimeout(() => {
                  setIsModalLoading(false);
               }, 100);
            } catch (error) {
               console.error('Error loading single view data:', error);
               setIsModalLoading(false); // Clear loading state on error
            }
         };

         return (
            <>
               {itemData.map((transaction, rowIndex) => {
                  const isOddRow = rowIndex % 2 !== 0;
                  const hasMultipleItems =
                     transaction.saleLineItems &&
                     Array.isArray(transaction.saleLineItems) &&
                     transaction.saleLineItems.length > 1;

                  // If formatType is with_item_detail and has multiple items, render all items in separate rows
                  if (formatType === 'with_item_detail' && hasMultipleItems) {
                     return (
                        <React.Fragment key={`row-${transaction.id || rowIndex}`}>
                           {transaction.saleLineItems.map((item, itemIndex) => {
                              const isFirstItem = itemIndex === 0;
                              const isLastItem = itemIndex === transaction.saleLineItems.length - 1;

                              return (
                                 <Tr
                                    key={`item-${item.id || itemIndex}-${transaction.id}`}
                                    color={textColor}
                                    cursor="pointer"
                                    fontSize={{ sm: '12px', lg: '14px' }}
                                    textAlign="center"
                                    alignItems="center"
                                    fontWeight={500}
                                    onClick={() => handledata(transaction)}
                                    bg={
                                       isOddRow
                                          ? 'rgba(0, 63, 140, 0.05)'
                                          : isFirstItem
                                          ? 'transparent'
                                          : 'rgba(0, 123, 255, 0.03)'
                                    }
                                    _dark={{
                                       bg: isOddRow
                                          ? 'rgba(2, 14, 56, 0.7)'
                                          : isFirstItem
                                          ? 'transparent'
                                          : 'rgba(0, 123, 255, 0.1)',
                                    }}
                                    _hover={{ bg: 'rgba(0, 63, 140, 0.08)' }}
                                    style={{ paddingLeft: paddingLeft }}
                                    borderLeft={!isFirstItem ? '3px solid' : undefined}
                                    borderLeftColor={!isFirstItem ? 'blue.200' : undefined}>
                                    {table
                                       .getVisibleFlatColumns()
                                       .sort((a, b) => {
                                          const colA = dynamicColumns.find((col) => col.id === a.id);
                                          const colB = dynamicColumns.find((col) => col.id === b.id);
                                          const orderA = parseInt(colA?.orderNo) || 0;
                                          const orderB = parseInt(colB?.orderNo) || 0;
                                          return orderA - orderB;
                                       })
                                       .map((column, cellIndex) => {
                                          const columnId = column.id;
                                          const cellValue = transaction[columnId];
                                          const columnDef = dynamicColumns.find((col) => col.id === column.id);
                                          const cellStyles = {
                                             color: columnDef?.color || textColor,
                                             fontWeight: columnDef?.isBold ? 'bold' : 'normal',
                                             fontStyle: columnDef?.isItalic ? 'italic' : 'normal',
                                             width: columnWidths[cellIndex],
                                          };

                                          const isNumeric =
                                             [
                                                'netAmount',
                                                'dueDays',
                                                'returnadjamount',
                                                'otheradjamount',
                                                'balanceamount',
                                                'recDate',
                                                'recBook',
                                                'itemRate',
                                                'qty',
                                                'totalQty1',
                                                'quantity',
                                                'basicAmount',
                                                'taxableAmount',
                                                'gstAmount',
                                             ].includes(columnId) || columnId.toLowerCase().includes('qty');

                                          const cellKey = `${transaction.id}-${column.id}-${cellIndex}-${itemIndex}`;

                                          // Handle item name column
                                          if (column.id === 'itemName') {
                                             return (
                                                <Td
                                                   borderColor={borderColor}
                                                   key={cellKey}
                                                   p="2px"
                                                   py="3px"
                                                   fontSize="13px"
                                                   whiteSpace="nowrap"
                                                   textOverflow="ellipsis"
                                                   color={textColor}
                                                   style={cellStyles}
                                                   maxW="200px"
                                                   overflow="hidden"
                                                   minW={{ sm: '150px', md: '200px', lg: 'auto' }}>
                                                   {item.itemName || '-'}
                                                </Td>
                                             );
                                          }

                                          // Handle qty columns
                                          if (
                                             columnId === 'qty' ||
                                             columnId === 'totalQty1' ||
                                             columnId === 'quantity' ||
                                             columnId.toLowerCase().includes('qty')
                                          ) {
                                             const qtyValue = item[columnId] || 0;
                                             return (
                                                <Td
                                                   p="2px"
                                                   py="5px"
                                                   fontSize="12px"
                                                   whiteSpace="nowrap"
                                                   textOverflow="ellipsis"
                                                   maxW="150px"
                                                   overflow="hidden"
                                                   style={cellStyles}
                                                   borderColor={borderColor}
                                                   key={columnId}
                                                   isNumeric
                                                   textAlign="right"
                                                   minW={{ sm: '150px', md: '200px', lg: 'auto' }}>
                                                   {Number(qtyValue).toLocaleString()}
                                                </Td>
                                             );
                                          }

                                          // Handle itemRate
                                          if (columnId === 'itemRate') {
                                             const itemRate = item.itemRate || 0;
                                             return (
                                                <Td
                                                   p="2px"
                                                   py="5px"
                                                   fontSize="12px"
                                                   whiteSpace="nowrap"
                                                   textOverflow="ellipsis"
                                                   maxW="150px"
                                                   overflow="hidden"
                                                   style={cellStyles}
                                                   borderColor={borderColor}
                                                   key={columnId}
                                                   isNumeric
                                                   textAlign="right"
                                                   minW={{ sm: '150px', md: '200px', lg: 'auto' }}>
                                                   ₹{Number(itemRate).toLocaleString()}
                                                </Td>
                                             );
                                          }

                                          // Handle serial numbers
                                          if (columnId === 'serialNo') {
                                             const serialNos = Array.isArray(item?.serialNo) ? item.serialNo : [];
                                             return (
                                                <Td
                                                   borderColor={borderColor}
                                                   key={columnId}
                                                   p="2px"
                                                   py="5px"
                                                   whiteSpace="nowrap"
                                                   textOverflow="ellipsis"
                                                   style={cellStyles}
                                                   maxW="150px"
                                                   overflow="hidden"
                                                   fontSize={{ sm: '12px', lg: '14px' }}
                                                   minW={{ sm: '150px', md: '200px', lg: 'auto' }}>
                                                   {serialNos.length > 0 ? (
                                                      <VStack align="start" spacing={1}>
                                                         {serialNos.map((serial, serialIndex) => (
                                                            <Text key={serialIndex}>{serial}</Text>
                                                         ))}
                                                      </VStack>
                                                   ) : (
                                                      'No Serial'
                                                   )}
                                                </Td>
                                             );
                                          }

                                          // For non-first items, only show item-related columns
                                          if (!isFirstItem) {
                                             if (
                                                [
                                                   'transactionAt',
                                                   'docno',
                                                   'accountname',
                                                   'mobile',
                                                   'daybookname',
                                                   'brokername',
                                                   'notes',
                                                ].includes(columnId)
                                             ) {
                                                return (
                                                   <Td
                                                      key={columnId}
                                                      borderColor={borderColor}
                                                      p="2px"
                                                      py="3px"
                                                      fontSize="12px"
                                                      color="gray.400"
                                                      minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                                   />
                                                );
                                             }
                                          }

                                          // Handle dates
                                          if (columnId.includes('Date') && transaction[columnId]) {
                                             return (
                                                <Td
                                                   p="2px"
                                                   py="5px"
                                                   whiteSpace="nowrap"
                                                   textOverflow="ellipsis"
                                                   maxW="150px"
                                                   overflow="hidden"
                                                   style={cellStyles}
                                                   borderColor={borderColor}
                                                   key={columnId}
                                                   fontSize={{ sm: '12px', lg: '14px' }}
                                                   minW={{ sm: '150px', md: '200px', lg: 'auto' }}>
                                                   {isFirstItem
                                                      ? new Date(transaction[columnId]).toLocaleDateString()
                                                      : null}
                                                </Td>
                                             );
                                          }

                                          // Handle numeric amounts
                                          if (
                                             ['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount'].includes(
                                                columnId,
                                             ) &&
                                             transaction[columnId] !== undefined
                                          ) {
                                             return (
                                                <Td
                                                   p="2px"
                                                   py="5px"
                                                   fontSize="12px"
                                                   whiteSpace="nowrap"
                                                   textOverflow="ellipsis"
                                                   style={cellStyles}
                                                   maxW="150px"
                                                   overflow="hidden"
                                                   borderColor={borderColor}
                                                   key={columnId}
                                                   minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                                   isNumeric
                                                   textAlign="right">
                                                   {isFirstItem
                                                      ? `₹${Number(transaction[columnId]).toLocaleString()}`
                                                      : null}
                                                </Td>
                                             );
                                          }

                                          // Handle boolean values
                                          if (typeof transaction[columnId] === 'boolean') {
                                             return (
                                                <Td
                                                   p="2px"
                                                   py="5px"
                                                   fontSize="14px"
                                                   whiteSpace="nowrap"
                                                   style={cellStyles}
                                                   textOverflow="ellipsis"
                                                   maxW="150px"
                                                   overflow="hidden"
                                                   borderColor={borderColor}
                                                   key={columnId}
                                                   minW={{ sm: '150px', md: '200px', lg: 'auto' }}>
                                                   {isFirstItem ? (transaction[columnId] ? 'Yes' : 'No') : null}
                                                </Td>
                                             );
                                          }

                                          // Default handling
                                          return (
                                             <Td
                                                key={columnId}
                                                borderColor={borderColor}
                                                maxW="150px"
                                                isNumeric={isNumeric}
                                                textAlign={isNumeric ? 'right' : 'left'}
                                                p="2px"
                                                py="5px"
                                                fontSize="12px"
                                                style={cellStyles}
                                                whiteSpace="nowrap"
                                                fontWeight={500}
                                                color={textColor}
                                                textOverflow="ellipsis"
                                                overflow="hidden"
                                                minW={{ sm: '150px', md: '200px', lg: 'auto' }}>
                                                {isFirstItem ? cellValue : null}
                                             </Td>
                                          );
                                       })}
                                 </Tr>
                              );
                           })}
                        </React.Fragment>
                     );
                  }

                  // Default rendering for single items or when formatType is not 'with_item_detail'
                  return (
                     <React.Fragment key={`row-${transaction.id || rowIndex}`}>
                        <Tr
                           color={textColor}
                           cursor="pointer"
                           fontSize={{ sm: '12px', lg: '14px' }}
                           textAlign="center"
                           alignItems="center"
                           fontWeight={500}
                           onClick={() => handledata(transaction)}
                           bg={isOddRow ? 'rgba(0, 63, 140, 0.05)' : 'transparent'}
                           _dark={{ bg: isOddRow ? 'rgba(2, 14, 56, 0.7)' : 'transparent' }}
                           _hover={{ bg: 'rgba(0, 63, 140, 0.08)' }}
                           style={{ paddingLeft: paddingLeft }}>
                           {table
                              .getVisibleFlatColumns()
                              .sort((a, b) => {
                                 const colA = dynamicColumns.find((col) => col.id === a.id);
                                 const colB = dynamicColumns.find((col) => col.id === b.id);
                                 const orderA = parseInt(colA?.orderNo) || 0;
                                 const orderB = parseInt(colB?.orderNo) || 0;
                                 return orderA - orderB;
                              })
                              .map((column, cellIndex) => {
                                 const columnId = column.id;
                                 const cellValue = transaction[columnId];
                                 const columnDef = dynamicColumns.find((col) => col.id === column.id);
                                 const cellStyles = {
                                    color: columnDef?.color || textColor,
                                    fontWeight: columnDef?.isBold ? 'bold' : 'normal',
                                    fontStyle: columnDef?.isItalic ? 'italic' : 'normal',
                                    width: columnWidths[cellIndex],
                                 };

                                 const isNumeric =
                                    [
                                       'netAmount',
                                       'dueDays',
                                       'returnadjamount',
                                       'otheradjamount',
                                       'balanceamount',
                                       'recDate',
                                       'recBook',
                                       'itemRate',
                                       'qty',
                                       'totalQty1',
                                       'quantity',
                                       'basicAmount',
                                       'taxableAmount',
                                       'gstAmount',
                                    ].includes(columnId) || columnId.toLowerCase().includes('qty');

                                 const cellKey = `${transaction.id}-${column.id}-${cellIndex}`;

                                 // Handle item name column
                                 if (column.id === 'itemName' && formatType === 'with_item_detail') {
                                    const itemName = transaction.saleLineItems?.[0]?.itemName || '-';
                                    return (
                                       <Td
                                          borderColor={borderColor}
                                          key={cellKey}
                                          p="2px"
                                          py="3px"
                                          fontSize="13px"
                                          whiteSpace="nowrap"
                                          textOverflow="ellipsis"
                                          color={textColor}
                                          style={cellStyles}
                                          maxW="200px"
                                          overflow="hidden"
                                          minW={{ sm: '150px', md: '200px', lg: 'auto' }}>
                                          {itemName}
                                       </Td>
                                    );
                                 }

                                 // Handle qty columns
                                 if (
                                    columnId === 'qty' ||
                                    columnId === 'totalQty1' ||
                                    columnId === 'quantity' ||
                                    columnId.toLowerCase().includes('qty')
                                 ) {
                                    const qtyValue =
                                       transaction.saleLineItems?.[0]?.[columnId] || transaction[columnId] || 0;
                                    return (
                                       <Td
                                          p="2px"
                                          py="5px"
                                          fontSize="12px"
                                          whiteSpace="nowrap"
                                          textOverflow="ellipsis"
                                          maxW="150px"
                                          overflow="hidden"
                                          style={cellStyles}
                                          borderColor={borderColor}
                                          key={columnId}
                                          isNumeric
                                          textAlign="right"
                                          minW={{ sm: '150px', md: '200px', lg: 'auto' }}>
                                          {Number(qtyValue).toLocaleString()}
                                       </Td>
                                    );
                                 }

                                 // Handle itemRate
                                 if (columnId === 'itemRate') {
                                    const itemRate = transaction.saleLineItems?.[0]?.itemRate || 0;
                                    return (
                                       <Td
                                          p="2px"
                                          py="5px"
                                          fontSize="12px"
                                          whiteSpace="nowrap"
                                          textOverflow="ellipsis"
                                          maxW="150px"
                                          overflow="hidden"
                                          style={cellStyles}
                                          borderColor={borderColor}
                                          key={columnId}
                                          isNumeric
                                          textAlign="right"
                                          minW={{ sm: '150px', md: '200px', lg: 'auto' }}>
                                          ₹{Number(itemRate).toLocaleString()}
                                       </Td>
                                    );
                                 }

                                 // Handle serial numbers
                                 if (columnId === 'serialNo') {
                                    const item = transaction.saleLineItems?.[0];
                                    const serialNos = Array.isArray(item?.serialNo) ? item.serialNo : [];
                                    return (
                                       <Td
                                          borderColor={borderColor}
                                          key={columnId}
                                          p="2px"
                                          py="5px"
                                          whiteSpace="nowrap"
                                          textOverflow="ellipsis"
                                          style={cellStyles}
                                          maxW="150px"
                                          overflow="hidden"
                                          fontSize={{ sm: '12px', lg: '14px' }}
                                          minW={{ sm: '150px', md: '200px', lg: 'auto' }}>
                                          {serialNos.length > 0 ? (
                                             <VStack align="start" spacing={1}>
                                                {serialNos.map((serial, serialIndex) => (
                                                   <Text key={serialIndex}>{serial}</Text>
                                                ))}
                                             </VStack>
                                          ) : (
                                             'No Serial'
                                          )}
                                       </Td>
                                    );
                                 }

                                 // Handle dates
                                 if (columnId.includes('Date') && transaction[columnId]) {
                                    return (
                                       <Td
                                          p="2px"
                                          py="5px"
                                          whiteSpace="nowrap"
                                          textOverflow="ellipsis"
                                          maxW="150px"
                                          overflow="hidden"
                                          style={cellStyles}
                                          borderColor={borderColor}
                                          key={columnId}
                                          fontSize={{ sm: '12px', lg: '14px' }}
                                          minW={{ sm: '150px', md: '200px', lg: 'auto' }}>
                                          {new Date(transaction[columnId]).toLocaleDateString()}
                                       </Td>
                                    );
                                 }

                                 // Handle numeric amounts
                                 if (
                                    ['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount'].includes(columnId) &&
                                    transaction[columnId] !== undefined
                                 ) {
                                    return (
                                       <Td
                                          p="2px"
                                          py="5px"
                                          fontSize="12px"
                                          whiteSpace="nowrap"
                                          textOverflow="ellipsis"
                                          style={cellStyles}
                                          maxW="150px"
                                          overflow="hidden"
                                          borderColor={borderColor}
                                          key={columnId}
                                          minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                          isNumeric
                                          textAlign="right">
                                          ₹{Number(transaction[columnId]).toLocaleString()}
                                       </Td>
                                    );
                                 }

                                 // Handle boolean values
                                 if (typeof transaction[columnId] === 'boolean') {
                                    return (
                                       <Td
                                          p="2px"
                                          py="5px"
                                          fontSize="14px"
                                          whiteSpace="nowrap"
                                          style={cellStyles}
                                          textOverflow="ellipsis"
                                          maxW="150px"
                                          overflow="hidden"
                                          borderColor={borderColor}
                                          key={columnId}
                                          minW={{ sm: '150px', md: '200px', lg: 'auto' }}>
                                          {transaction[columnId] ? 'Yes' : 'No'}
                                       </Td>
                                    );
                                 }

                                 // Default handling
                                 return (
                                    <Td
                                       key={columnId}
                                       borderColor={borderColor}
                                       maxW="150px"
                                       isNumeric={isNumeric}
                                       textAlign={isNumeric ? 'right' : 'left'}
                                       p="2px"
                                       py="3px"
                                       px={columnId === 'daybookname' ? 3 : 1}
                                       fontSize="12px"
                                       style={cellStyles}
                                       whiteSpace="nowrap"
                                       fontWeight={500}
                                       color={textColor}
                                       textOverflow="ellipsis"
                                       overflow="hidden"
                                       minW={{ sm: '150px', md: '200px', lg: 'auto' }}>
                                       {cellValue}
                                    </Td>
                                 );
                              })}
                        </Tr>
                     </React.Fragment>
                  );
               })}
            </>
         );
      },
      [table, textColor, borderColor, expandedItems, toggleExpand, dynamicColumns, columnWidths, formatType],
   );

   const renderReportContent = () => {
      if (isLoading) {
         return (
            <Tr>
               <Td colSpan={table.getVisibleFlatColumns().length} textAlign="center" py="100px">
                  <Loader />
               </Td>
            </Tr>
         );
      }

      if (!salesReport || salesReport.length === 0) {
         return (
            <Tr justifyContent="center" alignItems="center" width="100%" height="100%">
               <Td
                  p="2px"
                  py="3px"
                  fontSize="10px"
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                  maxW="150px"
                  overflow="hidden"
                  colSpan={table.getVisibleFlatColumns().length}
                  justifyContent="center"
                  alignItems="center"
                  width="100%">
                  <Grid justifyContent="center" alignItems="center" gap={9}>
                     <img src={nodata} alt="No Data Found" width="200px" height="200px" />
                     <GridItem justifySelf="center">
                        <label>No sales data found</label>
                     </GridItem>
                  </Grid>
               </Td>
            </Tr>
         );
      }

      // Handle custom dynamic report formats
      if (dynamicValues && dynamicValues.includes(reportType)) {
         const selectedFormat = dynamicOptions.find((opt) => opt.value === reportType);
         const viewJson = selectedFormat?.viewJson || [];
         const formatDefName = selectedFormat?.formatData?.defName;

         // Check if it's a supported sales report type
         const isSalesReturnDetailDate =
            formatDefName === 'SalesReturnDetailDate' || formatDefName === 'SalesReturnDetailBroker';
         const isSalesReturnDetailParty = formatDefName === 'SalesReturnDetailParty';

         if (!isSalesReturnDetailDate && !isSalesReturnDetailParty) {
            return null;
         }

         const convertBackendColor = (backendColor) => {
            if (!backendColor) return null;
            if (backendColor.startsWith('#')) return backendColor;
            if (backendColor.startsWith('0xff')) return '#' + backendColor.substring(4);
            if (/^[0-9A-Fa-f]{6}$/.test(backendColor)) return '#' + backendColor;
            return backendColor;
         };

         const shouldShowColumn = (columnId) => {
            const columnsToHide = [];
            if (reportType === 'date') {
               columnsToHide.push('date');
            } else if (reportType === 'party') {
               columnsToHide.push('party', 'partyName');
            } else if (reportType === 'broker') {
               columnsToHide.push('broker', 'brokerName');
            }

            if (columnsToHide.includes(columnId)) {
               return false;
            }

            const viewItem = viewJson.find(
               (item) =>
                  item.defName?.toLowerCase() === columnId.toLowerCase() ||
                  item.title?.toLowerCase() === columnId.toLowerCase(),
            );

            if (viewItem) {
               return viewItem.isShow === true || viewItem.isShow === 'true';
            }

            return true;
         };

         const renderTableHeaders = (bgColor = '#F5F5F5', darkBg = '#080238') => {
            return (
               <Tr>
                  {table.getHeaderGroups().map((headerGroup) => (
                     <React.Fragment key={headerGroup.id}>
                        {headerGroup.headers.map((header) => {
                           if (!shouldShowColumn(header.column.id)) return null;

                           const columnDef = dynamicColumns.find((col) => col.id === header.column.id);
                           const viewItem = viewJson.find(
                              (item) =>
                                 item.title?.toLowerCase() === header.column.id.toLowerCase() ||
                                 item.defName?.toLowerCase() === header.column.id.toLowerCase(),
                           );

                           const headerStyles = {
                              color: viewItem?.color ? convertBackendColor(viewItem.color) : textColor,
                              fontWeight: viewItem?.isBold ? 'bold' : 'normal',
                              fontStyle: viewItem?.isItalic ? 'italic' : 'normal',
                           };

                           const isNumericColumn = columnDef?.isNumeric;

                           return (
                              <Th
                                 borderTop="1px solid"
                                 key={header.id}
                                 colSpan={header.colSpan}
                                 borderColor={borderColor}
                                 cursor="pointer"
                                 p="2px"
                                 style={headerStyles}
                                 py="4px"
                                 fontSize="10px"
                                 _dark={{ bg: darkBg }}
                                 fontWeight="600"
                                 bg={bgColor}
                                 position="sticky"
                                 top="-1"
                                 zIndex="1"
                                 color={textColor}
                                 isNumeric={isNumericColumn}
                                 whiteSpace="nowrap"
                                 onClick={header.column.getToggleSortingHandler()}>
                                 <Flex
                                    justifyContent={isNumericColumn ? 'flex-end' : 'space-between'}
                                    align="center"
                                    fontSize={{ sm: '10px', lg: '12px' }}
                                    color={textColor}>
                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                    {{
                                       asc: '↑',
                                       desc: '↓',
                                    }[header.column.getIsSorted()] ?? null}
                                 </Flex>
                              </Th>
                           );
                        })}
                     </React.Fragment>
                  ))}
               </Tr>
            );
         };

         // RENDER SALES DATE FORMAT
         if (isSalesReturnDetailDate) {
            return salesReport.map((companyData, companyIndex) => {
               const companyItems = companyData.data || [];

               const groupedByDate = companyItems.reduce((acc, item) => {
                  const date = item.transactionAt || 'No Date';
                  if (!acc[date]) acc[date] = [];
                  acc[date].push(item);
                  return acc;
               }, {});

               return (
                  <React.Fragment key={`company-${companyData.companyId || companyIndex}`}>
                     {/* Company Header */}
                     <Tr bg="#F1F0E8" _dark={{ bg: 'blue.700' }} fontWeight="bold" color={textColor}>
                        <Td
                           colSpan={table.getVisibleFlatColumns().filter((col) => shouldShowColumn(col.id)).length}
                           p="2px"
                           py="3px"
                           fontSize="10px"
                           whiteSpace="nowrap"
                           textOverflow="ellipsis"
                           color={textColor}>
                           <Flex justifyContent="space-between" alignItems="center">
                              <Text fontWeight="bold" fontSize="lg">
                                 {companyData.companyName}
                              </Text>
                              <Flex gap={4}>
                                 <Text fontWeight="600" fontSize="15px">
                                    Qty:{' '}
                                    {companyItems.reduce((sum, t) => sum + (t?.totalQty1 || 0), 0).toLocaleString()}
                                 </Text>
                                 <Text fontWeight="bold" fontSize="15px">
                                    Amt: ₹
                                    {companyItems.reduce((sum, t) => sum + (t?.netAmount || 0), 0).toLocaleString()}
                                 </Text>
                              </Flex>
                           </Flex>
                        </Td>
                     </Tr>

                     {renderTableHeaders()}

                     {Object.entries(groupedByDate).map(([date, dateItems]) => (
                        <React.Fragment key={`date-group-${date}`}>
                           <Tr bg="gray.100" _dark={{ bg: 'gray.700' }}>
                              <Td
                                 colSpan={table.getAllColumns().filter((c) => shouldShowColumn(c.id)).length}
                                 p="2px"
                                 py="3px"
                                 color={'#000'}
                                 fontWeight="bold"
                                 fontSize="sm">
                                 {date}
                              </Td>
                           </Tr>
                           {renderDataRows(dateItems, table, borderColor, shouldShowColumn, viewJson)}
                        </React.Fragment>
                     ))}

                     {companyItems.length > 0 && (
                        <Tr bg="#F1F0E8" _dark={{ bg: 'blue.700' }} fontWeight="bold" color={textColor}>
                           {table
                              .getAllColumns()
                              .filter((column) => shouldShowColumn(column.id))
                              .map((column, colIndex, visibleCols) => {
                                 const cellKey = `grand-total-${column.id}-${colIndex}`;

                                 if (column.id === visibleCols[0]?.id) {
                                    return (
                                       <Td
                                          key={cellKey}
                                          p="2px"
                                          py="5px"
                                          fontSize="15px"
                                          fontWeight={700}
                                          borderColor="gray.200"
                                          minW={{ sm: '150px', md: '200px', lg: 'auto' }}>
                                          Grand Total
                                       </Td>
                                    );
                                 }

                                 if (column.id === 'qty' || column.id.toLowerCase().includes('qty')) {
                                    const grandTotalQty = companyItems.reduce((sum, t) => {
                                       const val = Number(t[column.id]);
                                       return sum + (isNaN(val) ? 0 : val);
                                    }, 0);

                                    return (
                                       <Td
                                          key={cellKey}
                                          p="2px"
                                          py="5px"
                                          fontSize="15px"
                                          fontWeight={700}
                                          borderColor="gray.200"
                                          isNumeric
                                          minW={{ sm: '150px', md: '200px', lg: 'auto' }}>
                                          {grandTotalQty.toLocaleString()}
                                       </Td>
                                    );
                                 }

                                 if (['netAmount', 'billAmount', 'taxableAmount', 'gstAmount'].includes(column.id)) {
                                    const total = companyItems.reduce((sum, t) => sum + (Number(t[column.id]) || 0), 0);
                                    return (
                                       <Td
                                          key={cellKey}
                                          p="2px"
                                          py="5px"
                                          fontSize="15px"
                                          fontWeight={700}
                                          borderColor="gray.200"
                                          isNumeric
                                          minW={{ sm: '150px', md: '200px', lg: 'auto' }}>
                                          ₹{total.toLocaleString()}
                                       </Td>
                                    );
                                 }

                                 return (
                                    <Td
                                       key={cellKey}
                                       p="2px"
                                       py="5px"
                                       borderColor="gray.200"
                                       minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                    />
                                 );
                              })}
                        </Tr>
                     )}
                  </React.Fragment>
               );
            });
         }

         // RENDER SALES PARTY FORMAT
         if (isSalesReturnDetailParty) {
            return salesReport.map((companyData, companyIndex) => {
               const companyItems = companyData.data || [];

               const groupedByParty = companyItems.reduce((acc, transaction) => {
                  const partyName = transaction.accountName || transaction.accountname || 'Unknown Party';
                  if (!acc[partyName]) {
                     acc[partyName] = {
                        accountName: partyName,
                        transactions: [],
                        totalQty: 0,
                        totalAmount: 0,
                     };
                  }
                  acc[partyName].transactions.push(transaction);
                  acc[partyName].totalQty += transaction.totalQty1 || 0;
                  acc[partyName].totalAmount += transaction.netAmount || 0;
                  return acc;
               }, {});

               const partyGroups = Object.values(groupedByParty);

               return (
                  <React.Fragment key={`company-${companyData.companyId || companyIndex}`}>
                     <Tr bg="#F1F0E8" _dark={{ bg: 'blue.700' }} fontWeight="bold" color={textColor}>
                        <Td
                           colSpan={table.getVisibleFlatColumns().filter((col) => shouldShowColumn(col.id)).length}
                           p="2px"
                           py="3px"
                           fontSize="10px"
                           whiteSpace="nowrap"
                           textOverflow="ellipsis"
                           color={textColor}>
                           <Flex justifyContent="space-between" alignItems="center">
                              <Text fontWeight="bold" fontSize="lg">
                                 {companyData.companyName}
                              </Text>
                              <Flex gap={4}>
                                 <Text fontWeight="600" fontSize="15px">
                                    Qty:{' '}
                                    {companyItems.reduce((sum, t) => sum + (t?.totalQty1 || 0), 0).toLocaleString()}
                                 </Text>
                                 <Text fontWeight="bold" fontSize="15px">
                                    Amt: ₹
                                    {companyItems.reduce((sum, t) => sum + (t?.netAmount || 0), 0).toLocaleString()}
                                 </Text>
                              </Flex>
                           </Flex>
                        </Td>
                     </Tr>

                     {partyGroups.map((partyGroup, partyIndex) => (
                        <React.Fragment key={`party-${companyIndex}-${partyIndex}`}>
                           <Tr
                              bg="#F5EEE6"
                              _dark={{ bg: 'blue.900' }}
                              cursor="pointer"
                              borderBottom={'1px solid'}
                              borderColor={borderColor}
                              onClick={() => toggleExpand(`party-${companyIndex}-${partyIndex}`)}>
                              <Td
                                 colSpan={
                                    table.getVisibleFlatColumns().filter((col) => shouldShowColumn(col.id)).length - 2
                                 }
                                 p="4px"
                                 fontSize={'14px'}
                                 fontWeight="bold"
                                 color={textColor}>
                                 <Flex alignItems="center" gap={2}>
                                    <Text>{partyGroup.accountName}</Text>
                                 </Flex>
                              </Td>
                              <Td p="4px" fontSize="14px" fontWeight="bold" color={textColor} textAlign="right">
                                 Qty: {partyGroup.totalQty.toLocaleString()}
                              </Td>
                              <Td p="4px" fontSize="14px" fontWeight="bold" color={textColor} textAlign="right">
                                 Amt: ₹{partyGroup.totalAmount.toLocaleString()}
                              </Td>
                           </Tr>

                           {expandedItems[`party-${companyIndex}-${partyIndex}`] !== false && (
                              <React.Fragment>
                                 {renderTableHeaders()}
                                 {renderDataRows(
                                    partyGroup.transactions,
                                    table,
                                    borderColor,
                                    shouldShowColumn,
                                    viewJson,
                                 )}
                              </React.Fragment>
                           )}
                        </React.Fragment>
                     ))}
                  </React.Fragment>
               );
            });
         }

         return null;
      }

      // RENDER PARTY FORMAT (Non-dynamic)
      if (reportType === REPORT_TYPE.party) {
         const singleCompanyMode = companyFilter.length === 1 || salesReport.length === 1;

         return salesReport.map((companyData, companyIndex) => (
            <React.Fragment key={`company-${companyData.companyId || companyIndex}`}>
               {/* Company row */}
               <Tr
                  bg="#F1F0E8"
                  _dark={{ bg: '#020E38' }}
                  color={textColor}
                  cursor="pointer"
                  onClick={() => toggleExpand(`company-${companyData.companyId}`)}>
                  <Td px={1} p={1} colSpan={table.getAllLeafColumns().length}>
                     <Flex justifyContent="space-between" alignItems="center">
                        <Text fontWeight="bold" fontSize="15px">
                           {companyData.companyName}
                        </Text>
                        <Flex alignItems="center" gap={4}>
                           <Text fontWeight="600" fontSize="15px">
                              Total Qty:{' '}
                              {companyData.data
                                 .reduce((sum, account) => sum + (account.totalQty1 || 0), 0)
                                 .toLocaleString()}
                           </Text>
                           <Text fontWeight="bold">
                              Total Amount: ₹
                              {companyData.data
                                 .reduce((sum, account) => sum + (account.totalNetAmt || 0), 0)
                                 .toLocaleString()}
                           </Text>
                           {expandedItems[`company-${companyData.companyId}`] ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        </Flex>
                     </Flex>
                  </Td>
               </Tr>

               {expandedItems[`company-${companyData.companyId}`] &&
                  companyData.data &&
                  Array.isArray(companyData.data) &&
                  companyData.data.map((accountData, accountIndex) => (
                     <React.Fragment key={`party-${accountData.accountId || accountIndex}`}>
                        {/* Account row */}
                        <Tr
                           bg="#F5EEE6"
                           _dark={{ bg: '#020938' }}
                           color={textColor}
                           cursor="pointer"
                           onClick={() => toggleExpand(`account-${accountData.accountId}`)}>
                           <Td px={1} p={1} colSpan={table.getAllLeafColumns().length}>
                              <Flex justifyContent="space-between" alignItems="center" pl={singleCompanyMode ? 0 : 4}>
                                 <Text fontWeight="bold" fontSize="15px">
                                    {accountData.accountName}
                                 </Text>
                                 <Flex alignItems="center" gap={4}>
                                    <Text fontWeight="600" fontSize="15px">
                                       Qty: {accountData.totalQty1?.toLocaleString() || '0'}
                                    </Text>
                                    <Text fontWeight="600" fontSize="15px">
                                       Amount: ₹{accountData.totalNetAmt?.toLocaleString() || '0'}
                                    </Text>
                                    {expandedItems[`account-${accountData.accountId}`] ? (
                                       <ChevronUpIcon />
                                    ) : (
                                       <ChevronDownIcon />
                                    )}
                                 </Flex>
                              </Flex>
                           </Td>
                        </Tr>

                        {/* Transaction rows - only show headers when expanded */}
                        {expandedItems[`account-${accountData.accountId}`] && (
                           <>
                              {table.getHeaderGroups().map((headerGroup) => (
                                 <Tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => {
                                       if (!header.column.getIsVisible()) return null;

                                       const columnId = header.column.id;
                                       const isNumericColumn = [
                                          'netAmount',
                                          'billAmount',
                                          'dueDays',
                                          'totalQty1',
                                          'itemRate',
                                          'returnadjamount',
                                          'otheradjamount',
                                          'balanceamount',
                                          'recDate',
                                          'recBook',
                                       ].includes(columnId);

                                       return (
                                          <Th
                                             borderTop="1px solid"
                                             key={header.id}
                                             colSpan={header.colSpan}
                                             borderColor={borderColor}
                                             cursor="pointer"
                                             p="2px"
                                             py="4px"
                                             fontSize="10px"
                                             _dark={{ bg: '#080238' }}
                                             fontWeight="600"
                                             bg={'#F5F5F5'}
                                             position="sticky"
                                             top="-1"
                                             zIndex="1"
                                             color={textColor}
                                             isNumeric={isNumericColumn}
                                             whiteSpace="nowrap"
                                             onClick={header.column.getToggleSortingHandler()}>
                                             <Flex
                                                justifyContent={isNumericColumn ? 'flex-end' : 'space-between'}
                                                align="center"
                                                fontSize={{ sm: '10px', lg: '12px' }}
                                                color={textColor}>
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                {{
                                                   asc: '↑',
                                                   desc: '↓',
                                                }[header.column.getIsSorted()] ?? null}
                                             </Flex>
                                          </Th>
                                       );
                                    })}
                                 </Tr>
                              ))}
                              {renderDataRows(accountData.data)}
                           </>
                        )}
                     </React.Fragment>
                  ))}
            </React.Fragment>
         ));
      }

      // DEFAULT RENDER (Date-based)
      return salesReport.map((dateItem, index) => {
         const itemData = dateItem.data?.flatMap((item) => item.data || []) || [];

         // Group items by date
         const groupedByDate = itemData.reduce((acc, item) => {
            const date = item.transactionAt || 'No Date';
            if (!acc[date]) {
               acc[date] = [];
            }
            acc[date].push(item);
            return acc;
         }, {});

         // Calculate company grand totals
         const companyTotals = {
            qty: itemData.reduce((sum, t) => {
               // Try different qty field names
               const val = Number(t.qty || t.totalQty1 || t.quantity || 0);
               return sum + (isNaN(val) ? 0 : val);
            }, 0),
            netAmount: itemData.reduce((sum, t) => sum + (Number(t.netAmount) || 0), 0),
            billAmount: itemData.reduce((sum, t) => sum + (Number(t.netAmount) || 0), 0),
            taxableAmount: itemData.reduce((sum, t) => sum + (Number(t.taxableAmount) || 0), 0),
            gstAmount: itemData.reduce((sum, t) => sum + (Number(t.gstAmount) || 0), 0),
         };

         return (
            <React.Fragment key={`date-${dateItem.date || index}`}>
               {/* Company Header Row with Grand Total */}
               <Tr bg="#F1F0E8" _dark={{ bg: 'blue.700' }} fontWeight="bold" color={textColor} pl={4}>
                  {table
                     .getAllColumns()
                     .filter((column) => column.getIsVisible())
                     .map((column, colIndex, visibleCols) => {
                        const cellKey = `company-header-${column.id}-${colIndex}`;

                        if (column.id === visibleCols[0].id) {
                           return (
                              <Td
                                 key={cellKey}
                                 p="2px"
                                 py="3px"
                                 fontSize="10px"
                                 whiteSpace="nowrap"
                                 textOverflow="ellipsis"
                                 maxW="150px"
                                 color={textColor}
                                 overflow="hidden">
                                 <Flex justifyContent="space-between" alignItems="center">
                                    <Text fontWeight="bold" fontSize="lg">
                                       {dateItem.companyName}
                                    </Text>
                                 </Flex>
                              </Td>
                           );
                        }

                        // Add grand totals to company row
                        if (
                           column.id === 'qty' ||
                           column.id === 'totalQty1' ||
                           column.id.toLowerCase().includes('qty')
                        ) {
                           return (
                              <Td
                                 key={cellKey}
                                 p="2px"
                                 py="3px"
                                 fontSize="12px"
                                 fontWeight={700}
                                 color={textColor}
                                 isNumeric
                                 textAlign="right">
                                 {companyTotals.qty}
                              </Td>
                           );
                        }

                        if (column.id === 'netAmount') {
                           return (
                              <Td
                                 key={cellKey}
                                 p="2px"
                                 py="3px"
                                 fontSize="12px"
                                 fontWeight={700}
                                 color={textColor}
                                 isNumeric
                                 textAlign="right">
                                 ₹{companyTotals.netAmount.toLocaleString()}
                              </Td>
                           );
                        }

                        if (column.id === 'billAmount') {
                           return (
                              <Td
                                 key={cellKey}
                                 p="2px"
                                 py="3px"
                                 fontSize="12px"
                                 fontWeight={700}
                                 color={textColor}
                                 isNumeric
                                 textAlign="right">
                                 ₹{companyTotals.billAmount.toLocaleString()}
                              </Td>
                           );
                        }

                        if (['taxableAmount', 'gstAmount'].includes(column.id)) {
                           return (
                              <Td
                                 key={cellKey}
                                 p="2px"
                                 py="3px"
                                 fontSize="12px"
                                 fontWeight={700}
                                 color={textColor}
                                 isNumeric
                                 textAlign="right">
                                 ₹{companyTotals[column.id].toLocaleString()}
                              </Td>
                           );
                        }

                        return <Td key={cellKey} p="2px" py="3px" color={textColor} />;
                     })}
               </Tr>

               {/* Table Headers */}
               {table.getHeaderGroups().map((headerGroup) => (
                  <Tr key={headerGroup.id}>
                     {headerGroup.headers.map((header) => {
                        if (!header.column.getIsVisible()) return null;

                        const columnId = header.column.id;
                        const isNumericColumn = [
                           'netAmount',
                           'billAmount',
                           'dueDays',
                           'totalQty1',
                           'returnadjamount',
                           'otheradjamount',
                           'balanceamount',
                           'recDate',
                           'recBook',
                        ].includes(columnId);

                        return (
                           <Th
                              borderTop="1px solid"
                              key={header.id}
                              colSpan={header.colSpan}
                              borderColor={borderColor}
                              cursor="pointer"
                              p="2px"
                              py="4px"
                              fontSize="10px"
                              _dark={{ bg: '#080238' }}
                              fontWeight="600"
                              bg={'#F5F5F5'}
                              position="sticky"
                              top="-1"
                              zIndex="1"
                              color={textColor}
                              isNumeric={isNumericColumn}
                              whiteSpace="nowrap"
                              onClick={header.column.getToggleSortingHandler()}>
                              <Flex
                                 justifyContent={isNumericColumn ? 'flex-end' : 'space-between'}
                                 align="center"
                                 fontSize={{ sm: '10px', lg: '12px' }}
                                 color={textColor}>
                                 {flexRender(header.column.columnDef.header, header.getContext())}
                                 {{
                                    asc: '↑',
                                    desc: '↓',
                                 }[header.column.getIsSorted()] ?? null}
                              </Flex>
                           </Th>
                        );
                     })}
                  </Tr>
               ))}

               {/* Grouped by Date */}
               {Object.entries(groupedByDate).map(([date, dateItems]) => {
                  // Calculate date-wise totals
                  const dateTotals = {
                     qty: dateItems.reduce((sum, t) => {
                        // Try different qty field names
                        const val = Number(t.qty || t.totalQty1 || t.quantity || 0);
                        return sum + (isNaN(val) ? 0 : val);
                     }, 0),
                     netAmount: dateItems.reduce((sum, t) => sum + (Number(t.netAmount) || 0), 0),
                     billAmount: dateItems.reduce((sum, t) => sum + (Number(t.netAmount) || 0), 0),
                     taxableAmount: dateItems.reduce((sum, t) => sum + (Number(t.taxableAmount) || 0), 0),
                     gstAmount: dateItems.reduce((sum, t) => sum + (Number(t.gstAmount) || 0), 0),
                  };

                  return (
                     <React.Fragment key={`date-group-${date}`}>
                        {/* Date Header Row with Date-wise Totals */}
                        <Tr bg="gray.100" _dark={{ bg: 'gray.700' }}>
                           {table
                              .getAllColumns()
                              .filter((column) => column.getIsVisible())
                              .map((column, colIndex, visibleCols) => {
                                 const cellKey = `date-header-${column.id}-${colIndex}`;

                                 if (column.id === visibleCols[0].id) {
                                    return (
                                       <Td
                                          key={cellKey}
                                          p="2px"
                                          py="3px"
                                          color={'#000'}
                                          fontWeight="bold"
                                          fontSize="sm">
                                          {date}
                                       </Td>
                                    );
                                 }

                                 // Add date-wise totals
                                 if (
                                    column.id === 'qty' ||
                                    column.id === 'totalQty1' ||
                                    column.id.toLowerCase().includes('qty')
                                 ) {
                                    return (
                                       <Td
                                          key={cellKey}
                                          p="2px"
                                          py="3px"
                                          color={'#000'}
                                          fontWeight="bold"
                                          fontSize="sm"
                                          isNumeric
                                          textAlign="right">
                                          {dateTotals.qty}
                                       </Td>
                                    );
                                 }

                                 if (column.id === 'netAmount') {
                                    return (
                                       <Td
                                          key={cellKey}
                                          p="2px"
                                          py="3px"
                                          color={'#000'}
                                          fontWeight="bold"
                                          fontSize="sm"
                                          isNumeric
                                          textAlign="right">
                                          ₹{dateTotals.netAmount.toLocaleString()}
                                       </Td>
                                    );
                                 }

                                 if (column.id === 'billAmount') {
                                    return (
                                       <Td
                                          key={cellKey}
                                          p="2px"
                                          py="3px"
                                          color={'#000'}
                                          fontWeight="bold"
                                          fontSize="sm"
                                          isNumeric
                                          textAlign="right">
                                          ₹{dateTotals.billAmount.toLocaleString()}
                                       </Td>
                                    );
                                 }

                                 if (['taxableAmount', 'gstAmount'].includes(column.id)) {
                                    return (
                                       <Td
                                          key={cellKey}
                                          p="2px"
                                          py="3px"
                                          color={'#000'}
                                          fontWeight="bold"
                                          fontSize="sm"
                                          isNumeric
                                          textAlign="right">
                                          ₹{dateTotals[column.id].toLocaleString()}
                                       </Td>
                                    );
                                 }

                                 return <Td key={cellKey} p="2px" py="3px" />;
                              })}
                        </Tr>

                        {/* Items for this date */}
                        {renderDataRows(dateItems, table, borderColor)}
                     </React.Fragment>
                  );
               })}
            </React.Fragment>
         );
      });
   };

   const memoizedReportContent = useMemo(
      () => renderReportContent(),
      [isLoading, salesReport, reportType, expandedItems, formatType, textColor, borderColor],
   );

   const handleShowHideFormOpen = async () => {
      setIsShowHideColumn(true);
      const formatData = reportformat.find((f) => f.title === reportType);
      if (formatData) {
         setIsEditShowHide(true);
         setEditData(formatData);
      } else {
         setIsEditShowHide(false);
         setEditData({});
      }
   };

   return (
      <>
         <Flex pr="25px" mb="8px" justifyContent="space-between" align="center" mt="50px" overflow="hidden">
            <Flex>
               <Image
                  src={back}
                  alt="Go Back"
                  width="15px"
                  onClick={() => window.history.back()}
                  style={{ cursor: 'pointer' }}
               />
               <Text color={textColor} fontSize="15px" fontWeight="700" lineHeight="100%" ml={5}>
                  Sale Return Detail
               </Text>
            </Flex>
            <Flex gap={2} justifyContent={'center'} alignItems={'center'}>
               <Box cursor={'pointer'} onClick={() => handleShowHideFormOpen()} mr={2}>
                  {isStaticReportType ? <FiPlusCircle size={22} /> : <LucideEdit size={22} />}
               </Box>
               <Button size="sm" variant="outline" onClick={expandAll} leftIcon={<MdExpand />}>
                  Expand All
               </Button>
               <Button size="sm" variant="outline" onClick={collapseAll} leftIcon={<MdCompress />}>
                  Collapse All
               </Button>
            </Flex>
         </Flex>

         <Box mt="15px" bg={backgroundColor} px={4} py={0} borderRadius="7px">
            <Flex alignItems="center" gap={3} justifyContent="end">
               <Box my={2} width="300px" fontSize="13px" fontWeight={500} display={{ base: 'none', xl: 'block' }}>
                  <GridItem className="seperator-width" mt={5}>
                     {renderDynamicDropdown(
                        'company',
                        'Company',
                        lastcompanyOptions,
                        { value: companyFilter, onChange: handleCompanyChange },
                        {},
                        textColor,
                        false,
                        'Select company',
                        true,
                        undefined,
                        true,
                     )}
                  </GridItem>
               </Box>

               <Box flex="1" display={{ base: 'none', xl: 'flex' }}>
                  <Flex alignItems="center" mt={3}>
                     <PeriodDatePicker
                        value={[new Date(dateRange.fromDate), new Date(dateRange.toDate)]}
                        onChange={handleDateRangeChange}
                        defaultPeriod="last7Days"
                        style={{ width: 250, marginRight: 20, borderRadius: 5, height: 35 }}
                     />
                  </Flex>

                  <Box my={2} zIndex={3} width={'150px'} fontSize={'13px'} fontWeight={500} mr={2}>
                     <label>Report Type</label>
                     <Select
                        options={reportTypeOptions}
                        formatOptionLabel={formatOptionLabel}
                        value={reportTypeOptions.find((option) => option.value === reportType)}
                        onChange={handleReportTypeChange}
                        placeholder="Select Report Type"
                        styles={{
                           control: (baseStyles, state) => ({
                              ...baseStyles,
                              minHeight: '30px',
                              height: '30px',
                              backgroundColor: controlBg,
                              borderColor: controlBorder,
                              boxShadow: state.isFocused ? focusBoxShadow : baseStyles.boxShadow,
                              '&:hover': {
                                 borderColor: controlBorderHover,
                              },
                           }),
                           valueContainer: (baseStyles) => ({
                              ...baseStyles,
                              height: '30px',
                              padding: '0 6px',
                              color: textColor,
                           }),
                           indicatorsContainer: (baseStyles) => ({
                              ...baseStyles,
                              height: '30px',
                           }),
                           singleValue: (baseStyles) => ({
                              ...baseStyles,
                              color: textColor,
                           }),
                           placeholder: (baseStyles) => ({
                              ...baseStyles,
                              color: placeholderColor,
                           }),
                           option: (baseStyles, state) => ({
                              ...baseStyles,
                              backgroundColor: state.isFocused ? optionHoverBg : optionBg,
                              color: textColor,
                              fontWeight: state.data?.isDynamic ? 'bold' : 'normal', // Style dynamic options differently
                              '&:hover': {
                                 backgroundColor: optionHoverBg,
                              },
                           }),
                           groupHeading: (baseStyles) => ({
                              ...baseStyles,
                              backgroundColor: menuBg,
                              border: `1px solid ${menuBorder}`,
                           }),
                           dropdownIndicator: (baseStyles) => ({
                              ...baseStyles,
                              color: indicatorColor,
                              '&:hover': {
                                 color: indicatorHoverColor,
                              },
                           }),
                           clearIndicator: (baseStyles) => ({
                              ...baseStyles,
                              color: indicatorColor,
                              '&:hover': {
                                 color: indicatorHoverColor,
                              },
                           }),
                        }}
                     />
                  </Box>

                  <Box my={2} zIndex={3} fontSize="13px" fontWeight={500}>
                     <label>Format</label>
                     <Select
                        options={FORMAT_TYPE_OPTIONS}
                        value={FORMAT_TYPE_OPTIONS.find((option) => option.value === formatType)}
                        onChange={(selectedOption) => {
                           setFormatType(selectedOption.value);
                           setExpandedItems({});
                        }}
                        placeholder="Select Format Type"
                        styles={{
                           control: (baseStyles, state) => ({
                              ...baseStyles,
                              minHeight: '30px',
                              height: '30px',
                              backgroundColor: controlBg,
                              borderColor: controlBorder,
                              boxShadow: state.isFocused ? focusBoxShadow : baseStyles.boxShadow,
                              '&:hover': {
                                 borderColor: controlBorderHover,
                              },
                           }),
                           valueContainer: (baseStyles) => ({
                              ...baseStyles,
                              height: '30px',
                              padding: '0 6px',
                              color: textColor,
                           }),
                           indicatorsContainer: (baseStyles) => ({
                              ...baseStyles,
                              height: '30px',
                           }),
                           singleValue: (baseStyles) => ({
                              ...baseStyles,
                              color: textColor,
                           }),
                           placeholder: (baseStyles) => ({
                              ...baseStyles,
                              color: placeholderColor,
                           }),
                           option: (baseStyles, state) => ({
                              ...baseStyles,
                              backgroundColor: state.isFocused ? optionHoverBg : optionBg,
                              color: textColor,
                              '&:hover': {
                                 backgroundColor: optionHoverBg,
                              },
                           }),
                           menu: (baseStyles) => ({
                              ...baseStyles,
                              backgroundColor: menuBg,
                              border: `1px solid ${menuBorder}`,
                           }),
                           dropdownIndicator: (baseStyles) => ({
                              ...baseStyles,
                              color: indicatorColor,
                              '&:hover': {
                                 color: indicatorHoverColor,
                              },
                           }),
                           clearIndicator: (baseStyles) => ({
                              ...baseStyles,
                              color: indicatorColor,
                              '&:hover': {
                                 color: indicatorHoverColor,
                              },
                           }),
                        }}
                     />
                  </Box>
               </Box>

               <Flex mr={2}>
                  <Box flex="1" mr={3}>
                     <MotionFlex
                        ref={wrapperRef}
                        align="center"
                        bg={bg}
                        borderRadius="full"
                        cursor="pointer"
                        px={expanded ? 3 : 0}
                        w={expanded ? '250px' : '40px'}
                        h="40px"
                        transition="all 0.3s ease"
                        onClick={() => setExpanded(true)}>
                        {isLoading ? (
                           <Spinner size="sm" color={icon} />
                        ) : (
                           <MotionSearchIcon
                              color={icon}
                              animate={{ rotate: expanded ? 0 : 90 }}
                              transition={{ duration: 0.3 }}
                              style={{
                                 marginRight: expanded ? '8px' : '10px',
                                 marginLeft: expanded ? '0' : '10px',
                              }}
                           />
                        )}
                        {expanded && (
                           <InputGroup width="200px">
                              <Input
                                 variant="unstyled"
                                 placeholder="Search ..."
                                 value={searchTerm}
                                 onChange={handleSearchChange}
                                 autoFocus
                                 color={icon}
                                 flex="1"
                              />
                           </InputGroup>
                        )}
                     </MotionFlex>
                  </Box>

                  <Button onClick={handleRefresh} _hover={{ bg: 'blue.50' }} mr={2}>
                     <MdOutlineRefresh size={20} mr={2} />
                  </Button>

                  <Button cursor={'pointer'} onClick={onFilterOpen} mr={2}>
                     <IoFilterOutline size={22} />
                  </Button>

                  <Tooltip label="Show/Hide Columns">
                     <IconButton
                        icon={<ViewIcon />}
                        onClick={() => setIsShowHideColumn(true)}
                        aria-label="Column Visibility"
                     />
                  </Tooltip>
               </Flex>

               <Flex mr={2}>
                  <Popover>
                     <PopoverTrigger>
                        <IconButton icon={<Image src={Download} boxSize="24px" />} aria-label="Download Options" />
                     </PopoverTrigger>
                     <PopoverContent width="220px" zIndex="10" bg={backgroundColor} mr={8}>
                        <PopoverBody p="">
                           <Button
                              width="100%"
                              variant="ghost"
                              _hover={{ bg: 'blue.100' }}
                              onClick={downloadExcel}
                              justifyContent="space-between"
                              mb={3}>
                              <img src={xls} alt="Download XLS" width="30px" />
                              Download Excel
                           </Button>
                           <Button
                              width="100%"
                              variant="ghost"
                              _hover={{ bg: 'blue.100' }}
                              onClick={downloadPDF}
                              justifyContent="space-between">
                              <img src={xls} alt="Download pdf" width="30px" mr />
                              Download PDF
                           </Button>
                        </PopoverBody>
                     </PopoverContent>
                  </Popover>
               </Flex>
               <Box ml={1}>
                  <Flex display={{ base: 'block', xl: 'none' }}>
                     <Button variant="unstyled" onClick={onOpen}>
                        <Image src={DotIcon} alt="Menu" width="25px" />
                     </Button>
                  </Flex>
               </Box>

               <Drawer isOpen={isOpen} placement="bottom" onClose={onClose} finalFocusRef={fromDateInputRef}>
                  <DrawerOverlay />
                  <DrawerContent borderTopRadius="20px">
                     <DrawerCloseButton />
                     <DrawerHeader borderBottomWidth="1px">Filters</DrawerHeader>
                     <DrawerBody>
                        <GridItem className="seperator-width">
                           {renderDynamicDropdown(
                              'company',
                              'Company',
                              lastcompanyOptions,
                              { value: companyFilter, onChange: handleCompanyChange },
                              {},
                              textColor,
                              false,
                              'Select company',
                              true,
                              undefined,
                              true,
                           )}
                        </GridItem>
                        <Box my={2} fontSize="13px" fontWeight={500}>
                           <label>Report Type</label>
                           <Select
                              options={REPORT_TYPE_OPTIONS}
                              value={REPORT_TYPE_OPTIONS.find((option) => option.value === reportType)}
                              onChange={(selectedOption) => {
                                 setReportType(selectedOption.value);
                                 setExpandedItems({});
                              }}
                              placeholder="Select Report Type"
                           />
                        </Box>
                        <Box alignItems="center" mt={3}>
                           <PeriodDatePicker
                              value={[new Date(dateRange.fromDate), new Date(dateRange.toDate)]}
                              onChange={handleDateRangeChange}
                              defaultPeriod="last7Days"
                              style={{ width: 250, marginRight: 20, borderRadius: 5, height: 35 }}
                           />
                        </Box>

                        <Button colorScheme="green" width="100%" onClick={onClose} mt={4}>
                           Apply Filters
                        </Button>
                     </DrawerBody>
                  </DrawerContent>
               </Drawer>
            </Flex>
         </Box>

         <Grid overflow="auto">
            <Card mt="15px" borderRadius="7px">
               <Box
                  overflowX="auto"
                  className="custom-scrollbar"
                  position="relative"
                  minH={{ base: '60vh', md: '65vh', xl: '70vh' }}
                  maxH={{ base: '60vh', md: '65vh', xl: '70vh' }}>
                  <Table variant="simple" color={textColor} mb="24px" mt="12px" p={5}>
                     <Thead></Thead>
                     <Tbody>{memoizedReportContent}</Tbody>
                  </Table>
               </Box>
            </Card>
         </Grid>

         <Columnshowhide
            isOpen={isShowHideColumn}
            onClose={() => setIsShowHideColumn(false)}
            data={columnsData}
            setShowHideData={setShowHideData}
         />

         <ColumnVisibilityModal
            isOpen={isColumnSelectorOpen}
            onClose={() => setIsColumnSelectorOpen(false)}
            columns={dynamicColumns}
            columnVisibility={columnVisibility}
            setColumnVisibility={table.setColumnVisibility}
         />

         <SalesPurchaseFilter
            type={reportType}
            isOpen={isFilterOpen}
            onClose={onFilterClose}
            companyIds={companyIds}
            fromDate={dateRange.fromDate}
            toDate={dateRange.toDate}
            entityType="SalesReturnDetail"
         />

         <ColumnShowHide
            isOpen={isShowHideColumn}
            onClose={() => setIsShowHideColumn(false)}
            data={columnsData}
            setShowHideData={setShowHideData}
            isedit={isEditShowHide}
            editData={editData}
            selectedFormat={reportTypeOptions.dynamicOptions}
            FixdefName1={'SalesReturnDetailDate'}
            FixdefName2={'SalesReturnDetailParty'}
            FixdefName3={'SalesReturnDetailBroker'}
            staticOption={staticOption}
            dynamicEditOption={dynamicEditOption}
         />

         {isOpenPurchaseReturnSingleView && (
            <div className="fixed inset-0 z-[999]">
               {isModalLoading ? (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                     <Loader />
                  </div>
               ) : viewDetail ? (
                  <SalesDetails
                     isOpen={isOpenPurchaseReturnSingleView}
                     onClose={handleSalesReturnDetailsClose}
                     singleEntityRights={saleInvoiceRight || saleReturnRight}
                     title={'Sales Return'}
                     entityType={'salereturn'}
                     singleEntity={viewDetail}
                     handlePrevPage={handlePrevPage}
                     handleNextPage={handleNextPage}
                     setIsEditFormOpen={setIsEditFormOpen}
                     setIsOpenSingleView={setIsOpenPurchaseReturnSingleView}
                     isEditFormOpen={isEditFormOpen}
                     // paginate={paginateData}
                     viewDetail={viewDetail}
                     lineItems={viewDetail?.lineitems}
                     billTerms={viewDetail?.purchasereturnbillteams}
                     onDelete={deletePurchaseReturn}
                  />
               ) : (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                     <Loader />
                  </div>
               )}
            </div>
         )}
      </>
   );
};

export default React.memo(SalesReturnDetail);
